import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { donationService } from '../services/api';
import '@fortawesome/fontawesome-free/css/all.min.css';

interface GroceryItem {
  itemId: string;
  itemName: string;
  quantity: number;
  unit: string;
  estimatedPrice: number;
}

interface GroceryDonationFormData {
  donorFirstName: string;
  donorLastName: string;
  donorEmail: string;
  donorPhone: string;
  donorAddress: string;
  donorCity: string;
  donorState: string;
  donorPostalCode: string;
  donorCountry: string;
  groceryItems: GroceryItem[];
  totalAmount: number;
  specialInstructions: string;
  donationType: 'online' | 'physical';
  dropOffDate?: string;
  dropOffTime?: string;
  paymentMethod?: 'ach' | 'creditcard';
  achAccountType?: 'Checking' | 'Savings';
  donationFrequency?: 'onetime' | 'recurring';
  recurringFrequency?: 'weekly' | 'monthly' | 'yearly';
  recurringStartDate?: string;
  recurringEndDate?: string;
  subscriptionEndType?: 'NoEndDate' | 'EndsOnDate' | 'EndsAfterTerms';
  numberOfTerms?: string;
}

const DonateGrocery: React.FC = () => {
  // Ref to prevent duplicate API calls
  const fetchingGroceryItemsRef = useRef(false);
  const fetchingStatesRef = useRef(false);
  const fetchingCountriesRef = useRef(false);

  // Helper function to get state code from state name
  const getStateCode = (stateName: string): string => {
    const stateMap: { [key: string]: string } = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
      'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
      'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
      'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
      'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
      'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
      'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
      'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
    };
    return stateMap[stateName] || stateName;
  };

  // Date helpers for recurring donations
  const formatDate = (d: Date) => d.toISOString().slice(0, 10);
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const todayStr = formatDate(today);
  const oneYearLaterInit = new Date(today);
  oneYearLaterInit.setFullYear(today.getFullYear() + 1);
  const oneYearLaterStr = formatDate(oneYearLaterInit);

  const validationSchema = Yup.object().shape({
    donorFirstName: Yup.string()
      .required('First name is required')
      .min(1, 'First name is required')
      .max(100, 'First name must be at most 100 characters'),
    donorLastName: Yup.string()
      .required('Last name is required')
      .min(1, 'Last name is required')
      .max(100, 'Last name must be at most 100 characters'),
    donorEmail: Yup.string().email('Invalid email').required('Email is required'),
    donorPhone: Yup.string()
      .required('Phone is required')
      .test('phone-digits-max-10', 'Enter a valid phone number (max 10 digits)', (val: string | undefined) => {
        const digits = (val || '').replace(/\D/g, '');
        return digits.length > 0 && digits.length <= 10;
      }),
    donorAddress: Yup.string(),
    donorCity: Yup.string(),
    donorState: Yup.string(),
    donorPostalCode: Yup.string(),
    donorCountry: Yup.string(),
    billingZipCode: Yup.string().when('donationType', {
      is: 'online',
      then: (schema) => schema
        .matches(/^\d{5}(?:-\d{4})?$/, 'Invalid ZIP code (use 5 digits or 5+4 format)')
        .required('Billing ZIP code is required'),
    }),
    donationType: Yup.string().required('Please select donation type').oneOf(['online', 'physical']),
    dropOffDate: Yup.string().when('donationType', {
      is: 'physical',
      then: (schema) => schema.required('Drop-off date is required'),
    }),
    dropOffTime: Yup.string().when('donationType', {
      is: 'physical',
      then: (schema) => schema.required('Drop-off time is required'),
    }),
    paymentMethod: Yup.string().when('donationType', {
      is: 'online',
      then: (schema) => schema.required('Payment method is required').oneOf(['ach', 'creditcard']),
    }),
    achAccountType: Yup.mixed().when(['donationType', 'paymentMethod'], {
      is: (donationType: string, paymentMethod: string) => donationType === 'online' && paymentMethod === 'ach',
      then: (schema) => schema.oneOf(['Checking', 'Savings']).required('Account Type is required'),
    }),
    groceryItems: Yup.array().of(
      Yup.object().shape({
        quantity: Yup.number().min(0, 'Quantity must be 0 or more'),
      })
    ),
    specialInstructions: Yup.string(),
    recurringStartDate: Yup.string().when(['donationType', 'donationFrequency'], {
      is: (donationType: string, donationFrequency: string) => donationType === 'online' && donationFrequency === 'recurring',
      then: (schema) => schema
        .required('Start Date is required')
        .matches(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format')
        .test('start-not-past', 'Start Date cannot be in the past', (val: string | undefined) => {
          if (!val) return false;
          return val >= todayStr;
        }),
    }),
    recurringEndDate: Yup.string().when(['donationType', 'donationFrequency', 'subscriptionEndType'], {
      is: (donationType: string, donationFrequency: string, subscriptionEndType: string) => 
        donationType === 'online' && donationFrequency === 'recurring' && subscriptionEndType === 'EndsOnDate',
      then: (schema) => schema
        .required('End Date is required')
        .matches(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format')
        .test('end-min-2-months', 'End Date must be at least 2 months after Start Date', function (val: string | undefined) {
          const start = (this.parent as any)?.recurringStartDate as string;
          if (!val || !start) return false;
          const startDate = new Date(start + 'T12:00:00');
          const endDate = new Date(val + 'T12:00:00');
          const minEndDate = new Date(startDate);
          minEndDate.setMonth(minEndDate.getMonth() + 2);
          return endDate >= minEndDate;
        }),
    }),
    subscriptionEndType: Yup.string().when(['donationType', 'donationFrequency'], {
      is: (donationType: string, donationFrequency: string) => donationType === 'online' && donationFrequency === 'recurring',
      then: (schema) => schema.oneOf(['NoEndDate', 'EndsOnDate', 'EndsAfterTerms']).required('Subscription End Type is required'),
    }),
    numberOfTerms: Yup.string().when(['donationType', 'donationFrequency', 'subscriptionEndType'], {
      is: (donationType: string, donationFrequency: string, subscriptionEndType: string) => 
        donationType === 'online' && donationFrequency === 'recurring' && subscriptionEndType === 'EndsAfterTerms',
      then: (schema) => schema
        .required('Number of Terms is required')
        .matches(/^\d+$/, 'Must be a positive number')
        .test('min-terms', 'Minimum 2 terms required', (val: string | undefined) => parseInt(val || '0', 10) >= 2)
        .test('max-terms', 'Maximum 9999 terms allowed', (val: string | undefined) => parseInt(val || '0', 10) <= 9999),
    }),
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [groceryItems, setGroceryItems] = useState<any[]>([]);
  const [formValues, setFormValues] = useState<any>(null);
  const [showMobileCart, setShowMobileCart] = useState(false);
  // reCAPTCHA state
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  // Load and render Google reCAPTCHA v2 (checkbox) widget
  useEffect(() => {
    // Only render reCAPTCHA on step 3 (payment step)
    if (currentStep !== 3) {
      return;
    }

    const ensureScript = (): Promise<void> => {
      return new Promise((resolve) => {
        const w = window as any;
        if (w.grecaptcha) return resolve();
        const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (existing) {
          existing.addEventListener('load', () => resolve());
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });
    };

    const renderWidget = () => {
      const w = window as any;
      const el = document.getElementById('recaptcha-container');
      if (!el) return;

      // Clear the container and reset state for fresh render
      el.innerHTML = '';
      setRecaptchaToken('');

      // Always use standard grecaptcha for v2 checkbox
      const pollUntilReady = () => {
        const api = (w && w.grecaptcha) ? w.grecaptcha : null;
        if (!api) {
          // Script not yet attached to window
          setTimeout(pollUntilReady, 150);
          return;
        }
        const doRender = () => {
          if (typeof api.render !== 'function') {
            // Not ready yet; try again shortly
            setTimeout(doRender, 150);
            return;
          }
          // Check if container already has a widget
          if (el.hasChildNodes()) return;
          
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (token: string) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback': () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch (e) {
            // In case of intermittent readiness issues
            if (process.env.NODE_ENV !== 'production') {
              console.warn('reCAPTCHA render failed, retrying...', e);
            }
            setTimeout(doRender, 200);
          }
        };

        if (typeof api.ready === 'function') {
          api.ready(doRender);
        } else {
          // Fallback if ready() is not available
          setTimeout(doRender, 0);
        }
      };

      pollUntilReady();
    };

    let mounted = true;
    ensureScript().then(() => {
      if (!mounted) return;
      // Defer a tick to ensure the container is mounted in DOM
      setTimeout(renderWidget, 100);
    });
    return () => {
      mounted = false;
    };
  }, [RECAPTCHA_SITE_KEY, currentStep]);

  useEffect(() => {
    loadGroceryItems();
    loadStatesForUS();
  }, []);

  const loadStatesForUS = async () => {
    // Prevent duplicate API calls
    if (fetchingStatesRef.current) return;
    if (states.length > 0) return;
    
    fetchingStatesRef.current = true;
    try {
      // Load states for United States (country_Id = 1)
      const stateData = await donationService.getStatesByCountry(1);
      setStates(stateData);
    } catch (error) {
      console.error('Error loading states:', error);
    } finally {
      fetchingStatesRef.current = false;
    }
  };

  useEffect(() => {
    loadCountries();
    
    // Set page title and favicon
    if (typeof document !== 'undefined') {
      document.title = 'Donate Groceries - Yugal Kunj';
      
      // Set favicon
      const setFavicon = (href: string) => {
        let link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = href;
      };
      
      // Use Yugal Kunj logo
      const YUGALKUNJ_LOGO = 'https://stssevastorage.blob.core.windows.net/ykwebsite/logo.png';
      try {
        const testImg = new Image();
        testImg.onload = () => setFavicon(YUGALKUNJ_LOGO);
        testImg.onerror = () => setFavicon('/logo512.png');
        testImg.src = YUGALKUNJ_LOGO;
      } catch {
        setFavicon('/logo512.png');
      }
    }
  }, []);


  const loadCountries = async () => {
    // Prevent duplicate API calls
    if (fetchingCountriesRef.current) return;
    if (countries.length > 0) return;
    
    fetchingCountriesRef.current = true;
    try {
      console.log('Loading countries...');
      const countryList = await donationService.getCountries();
      console.log('Countries loaded:', countryList);
      setCountries(countryList);
    } catch (error) {
      console.error('Error loading countries:', error);
    } finally {
      fetchingCountriesRef.current = false;
    }
  };

  const loadGroceryItems = async () => {
    // Prevent duplicate API calls
    if (fetchingGroceryItemsRef.current) return;
    if (groceryItems.length > 0) {
      setItemsLoading(false);
      return;
    }
    
    fetchingGroceryItemsRef.current = true;
    try {
      const items = await donationService.getGroceryItems();
      if (items && items.length > 0) {
        setGroceryItems(items);
      }
    } catch (error) {
      console.error('Error loading grocery items:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to Load Items',
        text: 'Unable to load grocery items. Please refresh the page.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setItemsLoading(false);
      fetchingGroceryItemsRef.current = false;
    }
  };

  const handleSubmit = async (values: GroceryDonationFormData) => {
    setLoading(true);

    // Require a valid reCAPTCHA token before submitting (only for online payments)
    if (values.donationType === 'online') {
      if (!recaptchaToken) {
        Swal.fire({
          icon: 'warning',
          title: 'reCAPTCHA Required',
          text: 'Please verify reCAPTCHA before submitting.',
          confirmButtonColor: '#f59e0b',
        });
        setLoading(false);
        return;
      }

      // Server-side verify the token
      const recaptchaResult = await donationService.verifyRecaptcha(recaptchaToken);
      if (!recaptchaResult || recaptchaResult.success !== true) {
        Swal.fire({
          icon: 'error',
          title: 'Verification Failed',
          text: 'reCAPTCHA verification failed. Please try again.',
          confirmButtonColor: '#ef4444',
        });
        try {
          const w = window as any;
          if (recaptchaWidgetId !== null && w.grecaptcha) {
            w.grecaptcha.reset(recaptchaWidgetId);
            setRecaptchaToken('');
          }
        } catch {}
        setLoading(false);
        return;
      }
    }

    try {
      const selectedItems = values.groceryItems.filter(item => Number(item.quantity) > 0);
      
      if (selectedItems.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Items Selected',
          text: 'Please add at least one item to your donation by clicking the + button to increase the quantity.',
        });
        setLoading(false);
        return;
      }

      const totalAmount = selectedItems.reduce((sum, item) => sum + (Number(item.quantity) * item.estimatedPrice), 0);

      // Handle Physical Drop-Off
      if (values.donationType === 'physical') {
        const donationData = {
          app: 'Donation',
          entity: 'Temple',
          firstName: values.donorFirstName,
          lastName: values.donorLastName,
          email: values.donorEmail,
          phoneNo: values.donorPhone,
          address: values.donorAddress,
          country: values.donorCountry,
          state: values.donorState,
          city: values.donorCity,
          postalCode: values.donorPostalCode,
          purposeOfDonation: 'Grocery Donation - Physical Drop-Off',
          paymentFor: selectedItems.map(item => item.itemName).join(', '),
          donationAmount: totalAmount,
          donationType: 'Grocery',
          paymentType: 'Physical Drop-Off',
          dropOffDate: values.dropOffDate,
          dropOffTime: values.dropOffTime,
          groceryItems: selectedItems.map(item => ({
            ItemId: item.itemId,
            ItemName: item.itemName,
            Quantity: item.quantity,
            Unit: item.unit,
            EstimatedPrice: item.estimatedPrice
          })),
          specialInstructions: values.specialInstructions,
        };

        console.log('Sending drop-off donation data:', donationData);
        const response = await donationService.addGroceryDonation(donationData);
        console.log('Drop-off API response:', response);

        if (response && (response.success || response.Success)) {
          // Send email with complete donation data
          await donationService.sendGroceryDonationEmail(donationData);

          Swal.fire({
            icon: 'success',
            title: 'Drop-Off Scheduled!',
            html: `
              <div class="text-center">
                <p class="text-lg font-semibold mb-2">Your physical drop-off has been scheduled!</p>
                <p class="text-gray-600 mb-2"><strong>Date:</strong> ${new Date(values.dropOffDate!).toLocaleDateString()}</p>
                <p class="text-gray-600 mb-2"><strong>Time:</strong> ${values.dropOffTime}</p>
                <p class="text-sm text-gray-500">A confirmation email has been sent to ${values.donorEmail}</p>
                <p class="text-sm text-gray-500 mt-2">Temple Address: Yugal Kunj - Radha Krishna Temple and Community Center, 2769 Duluth Hwy, Duluth, GA 30096</p>
              </div>
            `,
            confirmButtonColor: '#10b981',
            confirmButtonText: 'Done',
          }).then(() => {
            // Reset reCAPTCHA
            try {
              const w = window as any;
              if (recaptchaWidgetId !== null && w.grecaptcha) {
                w.grecaptcha.reset(recaptchaWidgetId);
                setRecaptchaToken('');
              }
            } catch {}
            window.location.href = '/donate-grocery';
          });
        } else {
          throw new Error(response?.message || 'Failed to schedule drop-off');
        }
      } 
      // Handle Online Donation with Payment Gateway (Authorize.Net)
      else if (values.donationType === 'online') {
        // Validate payment details
        if (values.paymentMethod === 'creditcard') {
          const cardNumber = (values as any).cardNumber;
          const expiryDate = (values as any).expiryDate;
          const cvv = (values as any).cvv;
          const cardholderName = (values as any).cardholderName;
          
          if (!cardNumber || !expiryDate || !cvv || !cardholderName) {
            Swal.fire({
              icon: 'warning',
              title: 'Missing Payment Details',
              text: 'Please fill in all credit card information.',
            });
            setLoading(false);
            return;
          }

          // Validate expiry date format and check if not expired
          if (expiryDate && expiryDate.includes('/')) {
            const [month, year] = expiryDate.split('/');
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear() % 100; // Get last 2 digits (e.g., 26 for 2026)
            const currentMonth = currentDate.getMonth() + 1;
            
            const expMonth = parseInt(month, 10);
            const expYear = parseInt(year, 10);
            
            // Validate month
            if (expMonth < 1 || expMonth > 12) {
              Swal.fire({
                icon: 'error',
                title: 'Invalid Expiry Date',
                text: 'Please enter a valid month (01-12).',
              });
              setLoading(false);
              return;
            }
            
            // Check if card is expired
            // If expYear < currentYear (e.g., 25 < 26), card is expired
            // If expYear === currentYear and expMonth < currentMonth, card is expired
            if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
              Swal.fire({
                icon: 'error',
                title: 'Card Expired',
                text: 'The credit card has expired. Please use a valid card.',
              });
              setLoading(false);
              return;
            }
          }
        } else if (values.paymentMethod === 'ach') {
          const bankName = (values as any).bankName;
          const accountNumber = (values as any).accountNumber;
          const routingNumber = (values as any).routingNumber;
          const accountHolderName = (values as any).accountHolderName;
          
          if (!bankName || !accountNumber || !routingNumber || !accountHolderName) {
            Swal.fire({
              icon: 'warning',
              title: 'Missing Payment Details',
              text: 'Please fill in all ACH information.',
            });
            setLoading(false);
            return;
          }
        }

        // Prepare Authorize.Net payment payload (matching RMS structure)
        const firstName = values.donorFirstName;
        const lastName = values.donorLastName;
        const description = `Grocery Donation - ${selectedItems.map(item => `${item.itemName} (${item.quantity})`).join(', ')}`;
        
        const authNetPayload: any = {
          app: 'Donation',
          entity: 'Temple',
          paymentType: values.paymentMethod === 'creditcard' ? 'cc' : 'ach',
          customerEmail: values.donorEmail,
          orderid: '0',
          amount: totalAmount.toString(),
          description: description,
          customer: {
            firstName: firstName,
            lastName: lastName,
            email: values.donorEmail,
            phone: values.donorPhone.replace(/\D/g, ''),
            company: 'string',
            notes: description,
            customerAccount: 'string',
            shippingSameAsBilling: true,
            billingAddress: {
              streetAddress1: '',
              streetAddress2: '-',
              city: '',
              stateCode: '',
              zipCode: (values as any).billingZipCode || '',
              country: 'US',
            },
            shippingAddress: {
              streetAddress1: '',
              streetAddress2: '-',
              city: '',
              stateCode: '',
              zipCode: (values as any).billingZipCode || '',
              country: 'US',
            },
          },
          creditcard: values.paymentMethod === 'creditcard' ? {
            creditCardNumber: ((values as any).cardNumber || '').replace(/\s/g, ''),
            expirationDate: (values as any).expiryDate,
            issuer: 'string',
            billingZipCode: (values as any).billingZipCode || '',
            customerId: 'string',
            cvv: (values as any).cvv,
          } : {
            creditCardNumber: 'string',
            expirationDate: 'string',
            issuer: 'string',
            billingZipCode: 'string',
            customerId: 'string',
            cvv: 'string',
          },
          ach: values.paymentMethod === 'ach' ? {
            isCheckingAccount: values.achAccountType === 'Checking' ? 'Y' : 'N',
            routingNumber: (values as any).routingNumber,
            accountNumber: (values as any).accountNumber,
            bankName: (values as any).bankName,
            customerId: 'string',
            isDefault: 'string',
          } : {
            isCheckingAccount: 'string',
            routingNumber: 'string',
            accountNumber: 'string',
            bankName: 'string',
            customerId: 'string',
            isDefault: 'string',
          },
          recurr: values.donationFrequency === 'recurring' ? (() => {
            // Calculate startDate
            const startDateToUse = new Date((values.recurringStartDate || new Date().toISOString().split('T')[0]) + 'T12:00:00');
            
            // Calculate endDate based on subscriptionEndType
            let calculatedEndDate: string;
            if (values.subscriptionEndType === 'NoEndDate') {
              // No end date - use 9998 months (833 years + 2 months) to stay within 9999 limit
              const farFutureDate = new Date(startDateToUse);
              farFutureDate.setFullYear(farFutureDate.getFullYear() + 833);
              farFutureDate.setMonth(farFutureDate.getMonth() + 2);
              calculatedEndDate = farFutureDate.toISOString();
            } else if (values.subscriptionEndType === 'EndsOnDate') {
              calculatedEndDate = new Date((values.recurringEndDate || '') + 'T12:00:00').toISOString();
            } else if (values.subscriptionEndType === 'EndsAfterTerms') {
              const numTerms = parseInt(values.numberOfTerms || '12', 10);
              const endDate = new Date(startDateToUse);
              endDate.setMonth(endDate.getMonth() + numTerms);
              calculatedEndDate = endDate.toISOString();
            } else {
              calculatedEndDate = new Date((values.recurringEndDate || '') + 'T12:00:00').toISOString();
            }
            
            return {
              paymentAmount: totalAmount.toString(),
              accountId: 'string',
              startDate: startDateToUse.toISOString(),
              executionFrequencyType: 'Monthly',
              executionFrequencyParameter: '1',
              endDate: calculatedEndDate,
              paymentSubType: 'string',
              invoiceNumber: 'string',
              firstPaymentAmount: totalAmount.toString(),
              firstPaymentDate: new Date().toISOString().split('T')[0],
              description: description,
            };
          })() : {
            paymentAmount: 'string',
            accountId: 'string',
            startDate: 'string',
            executionFrequencyType: 'string',
            executionFrequencyParameter: 'string',
            endDate: 'string',
            paymentSubType: 'string',
            invoiceNumber: 'string',
            firstPaymentAmount: 'string',
            firstPaymentDate: 'string',
            description: 'string',
          }
        }

        try {
          let paymentResponse: any;

          // Call appropriate Authorize.Net endpoint
          if (values.donationFrequency === 'recurring') {
            paymentResponse = await donationService.authNetRecurringPayment(authNetPayload, '0');
          } else {
            paymentResponse = await donationService.authNetOneTimePayment(authNetPayload);
          }

          // Parse response (matching RMS pattern)
          const respMessages = (
            paymentResponse?.messages ||
            paymentResponse?.Messages ||
            paymentResponse?.createTransactionResponse?.messages ||
            paymentResponse?.message ||
            paymentResponse?.Message ||
            {}
          );
          const resultCode = String(
            respMessages?.resultCode ?? paymentResponse?.resultCode ?? ''
          );
          const msgArray = Array.isArray((respMessages as any)?.message)
            ? (respMessages as any).message
            : ((respMessages as any)?.message ? [(respMessages as any).message] : []);
          const messageCode = (msgArray[0]?.code) || (paymentResponse?.createTransactionResponse?.messages?.message?.code);
          let messageText = '';
          if (typeof respMessages === 'string') {
            messageText = respMessages;
          } else {
            messageText = (msgArray[0]?.text || msgArray[0]?.description) || '';
          }
          // Fallbacks for top-level string messages
          if (!messageText && typeof (paymentResponse as any)?.Message === 'string') {
            messageText = (paymentResponse as any).Message;
          }
          if (!messageText && typeof (paymentResponse as any)?.message === 'string') {
            messageText = (paymentResponse as any).message;
          }

          // Check for subscription ID in recurring payment response (matching DonationForm)
          const hasSubscriptionId = typeof paymentResponse?.aRBCreateSubscriptionResponse?.subscriptionId === 'string' && 
                                   paymentResponse.aRBCreateSubscriptionResponse.subscriptionId.length > 0;
          
          const ok = (
            paymentResponse?.success === true ||
            paymentResponse?.isSuccess === true ||
            paymentResponse?.status === 'OK' ||
            ['ok', 'OK', 'Ok'].includes(resultCode) ||
            messageCode === 'I00001' ||
            // For recurring payments, check ARB subscription response (matching DonationForm)
            (values.donationFrequency === 'recurring' && hasSubscriptionId)
          );

          if (!ok) {
            // Handle validation errors from API
            if (paymentResponse?.errors) {
              const errorMessages = Object.entries(paymentResponse.errors)
                .map(([field, messages]: [string, any]) => {
                  const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
                  const errorList = Array.isArray(messages) ? messages : [messages];
                  return `<strong>${fieldName}:</strong> ${errorList.join(', ')}`;
                })
                .join('<br/>');
              
              Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                html: `<div class="text-start">${errorMessages}</div>`,
                confirmButtonColor: '#ef4444',
              });
              setLoading(false);
              return;
            }
            
            // Extract detailed error information from Authorize.Net response
            let errorDetails = messageText || 'Payment processing failed. Please try again.';
            const errorMessages: string[] = [];
            
            // Check for meta.errors structure (from backend validation)
            if (paymentResponse?.meta?.errors?.errorMessages) {
              const metaErrors = Array.isArray(paymentResponse.meta.errors.errorMessages)
                ? paymentResponse.meta.errors.errorMessages
                : [paymentResponse.meta.errors.errorMessages];
              metaErrors.forEach((err: any) => {
                let errMsg = err?.message || err?.text || '';
                // Try to extract user-friendly message from nested JSON in the error message
                if (errMsg && errMsg.includes('"message":[{')) {
                  try {
                    // Extract the nested JSON part that contains the actual error
                    const jsonMatch = errMsg.match(/"message":\s*\[\s*\{[^}]+\}\s*\]/);
                    if (jsonMatch) {
                      const innerJson = JSON.parse(`{${jsonMatch[0]}}`);
                      if (innerJson?.message?.[0]?.text) {
                        errMsg = innerJson.message[0].text;
                      }
                    }
                  } catch {
                    // If JSON parsing fails, try regex extraction
                    const textMatch = errMsg.match(/"text"\s*:\s*"([^"]+)"/);
                    if (textMatch && textMatch[1]) {
                      errMsg = textMatch[1];
                    }
                  }
                }
                if (errMsg) {
                  const errorCode = paymentResponse.meta.errors.errorCode || err?.errorCode || '';
                  errorMessages.push(errorCode ? `${errMsg} (Code: ${errorCode})` : errMsg);
                }
              });
            }
            
            // Check for transaction response errors
            if (paymentResponse?.createTransactionResponse?.transactionResponse?.errors) {
              const errors = paymentResponse.createTransactionResponse.transactionResponse.errors;
              const errorArray = Array.isArray(errors) ? errors : [errors];
              errorArray.forEach((err: any) => {
                if (err?.errorText) {
                  errorMessages.push(`${err.errorText} (Code: ${err.errorCode || 'N/A'})`);
                }
              });
            }
            
            // Check for messages array with error details
            if (paymentResponse?.messages?.message) {
              const messages = Array.isArray(paymentResponse.messages.message)
                ? paymentResponse.messages.message
                : [paymentResponse.messages.message];
              messages.forEach((msg: any) => {
                const msgText = msg?.text || msg?.description || '';
                const msgCode = msg?.code || '';
                if (msgText) {
                  errorMessages.push(msgCode ? `${msgText} (Code: ${msgCode})` : msgText);
                }
              });
            }
            
            // Check for ARB subscription response errors (for recurring payments)
            if (paymentResponse?.aRBCreateSubscriptionResponse?.messages?.message) {
              const arbMessages = Array.isArray(paymentResponse.aRBCreateSubscriptionResponse.messages.message)
                ? paymentResponse.aRBCreateSubscriptionResponse.messages.message
                : [paymentResponse.aRBCreateSubscriptionResponse.messages.message];
              arbMessages.forEach((msg: any) => {
                const msgText = msg?.text || msg?.description || '';
                const msgCode = msg?.code || '';
                if (msgText) {
                  errorMessages.push(msgCode ? `${msgText} (Code: ${msgCode})` : msgText);
                }
              });
            }
            
            // Check for top-level error messages
            if (paymentResponse?.errorMessages) {
              const errors = Array.isArray(paymentResponse.errorMessages) 
                ? paymentResponse.errorMessages 
                : [paymentResponse.errorMessages];
              errors.forEach((err: any) => {
                if (typeof err === 'string') {
                  errorMessages.push(err);
                } else if (err?.message) {
                  errorMessages.push(err.message);
                }
              });
            }
            
            // Check for errors array in response
            if (paymentResponse?.errors) {
              const errors = Array.isArray(paymentResponse.errors)
                ? paymentResponse.errors
                : [paymentResponse.errors];
              errors.forEach((err: any) => {
                if (typeof err === 'string') {
                  errorMessages.push(err);
                } else if (err?.message) {
                  // Include error code if available
                  const errorCode = err?.errorCode ? ` (Code: ${err.errorCode})` : '';
                  errorMessages.push(`${err.message}${errorCode}`);
                } else if (err?.text) {
                  const errorCode = err?.errorCode || err?.code ? ` (Code: ${err.errorCode || err.code})` : '';
                  errorMessages.push(`${err.text}${errorCode}`);
                }
              });
            }
            
            // Use collected error messages if available
            if (errorMessages.length > 0) {
              errorDetails = errorMessages.join('\n');
            } else if (messageText) {
              errorDetails = messageText;
            }
            
            // Show payment error message
            Swal.fire({
              icon: 'error',
              title: 'Payment Failed',
              html: `
                <div style="text-align: left;">
                  <p style="margin-bottom: 1rem;">${errorDetails.replace(/\n/g, '<br/>')}</p>
                  ${paymentResponse?.createTransactionResponse?.transactionResponse?.transId 
                    ? `<p style="font-size: 0.875rem; color: #6b7280;">Transaction ID: ${paymentResponse.createTransactionResponse.transactionResponse.transId}</p>` 
                    : ''}
                </div>
              `,
              confirmButtonColor: '#ef4444',
              width: '500px',
            });
            setLoading(false);
            return;
          }

          // Extract transaction ID and subscription ID (matching DonationForm)
          const transactionId = paymentResponse?.transactionId || 
                               paymentResponse?.transId || 
                               paymentResponse?.createTransactionResponse?.transactionResponse?.transId ||
                               '';
          const subscriptionId = paymentResponse?.aRBCreateSubscriptionResponse?.subscriptionId || 
                                paymentResponse?.subscriptionId || '';

          // Save donation record to database
          const donationData = {
            app: 'Donation',
            entity: 'Temple',
            firstName: firstName,
            lastName: lastName,
            email: values.donorEmail,
            phoneNo: values.donorPhone,
            address: values.donorAddress,
            country: values.donorCountry,
            state: values.donorState,
            city: values.donorCity,
            postalCode: values.donorPostalCode,
            purposeOfDonation: `Grocery Donation - Online Payment${values.donationFrequency === 'recurring' ? ' (Recurring)' : ''}`,
            paymentFor: selectedItems.map(item => `${item.itemName} (${item.quantity})`).join(', '),
            donationAmount: totalAmount,
            donationType: 'Grocery',
            paymentType: `Online Payment - ${values.paymentMethod === 'creditcard' ? 'Credit Card' : 'ACH'}`,
            groceryItems: selectedItems.map(item => ({
              ItemId: item.itemId,
              ItemName: item.itemName,
              Quantity: item.quantity,
              Unit: item.unit,
              EstimatedPrice: item.estimatedPrice
            })),
            specialInstructions: values.specialInstructions,
            transactionId: transactionId,
            subscriptionId: subscriptionId,
          };

          const dbResponse = await donationService.addGroceryDonation(donationData);

          if (dbResponse && (dbResponse.success || dbResponse.Success)) {
            // Send confirmation email and capture receipt URL
            let receiptUrl = '';
            try {
              // Build email data for DonationGrocery template
              // Template placeholders: ##Name##, ##PaymentType##, ##Date##, ##Amount##, ##ItemData##, ##Email##, ##Phone##, ##Address##, ##Instructions##
              const isRecurring = values.donationFrequency === 'recurring';
              
              // Build item data string for email
              const itemDataStr = selectedItems
                .map((item: any) => `• ${item.itemName}: ${item.quantity} ${item.unit} ($${(item.quantity * item.estimatedPrice).toFixed(2)})`)
                .join('\n');
              
              // Build payment type string
              let paymentTypeStr = values.donationFrequency === 'recurring' ? 'Recurring Monthly' : 'One-Time';
              paymentTypeStr += ` - ${values.paymentMethod === 'creditcard' ? 'Credit Card' : 'ACH'}`;
              if (isRecurring) {
                if (values.subscriptionEndType === 'NoEndDate') {
                  paymentTypeStr += ' (No End Date)';
                } else if (values.subscriptionEndType === 'EndsOnDate') {
                  paymentTypeStr += ` (Until ${new Date(values.recurringEndDate || '').toLocaleDateString('en-US')})`;
                } else if (values.subscriptionEndType === 'EndsAfterTerms') {
                  paymentTypeStr += ` (${values.numberOfTerms} payments)`;
                }
              }
              
              // Build instructions section
              const instructionsHtml = values.specialInstructions 
                ? `<h3 style="color:#2563eb;margin-top:20px;">Special Instructions:</h3><p style="background:#fef3c7;padding:10px;border-radius:5px;">${values.specialInstructions}</p>`
                : '';
              
              const emailData: any = {
                Email: values.donorEmail,
                ContactNo: values.donorPhone,
                PhoneNo: values.donorPhone,
                UserName: `${firstName} ${lastName}`.trim(),
                FirstName: firstName,
                LastName: lastName,
                DonationDate: new Date().toISOString(),
                Amount: String(totalAmount.toFixed(2)),
                Address: values.donorAddress || '',
                City: values.donorCity || '',
                State: values.donorState || '',
                PostalCode: values.donorPostalCode || '',
                Country: values.donorCountry || '',
                // Additional fields for DonationGrocery template
                PaymentType: paymentTypeStr,
                PaymentFor: itemDataStr,
                ItemData: itemDataStr,
                Instructions: instructionsHtml,
              };
              
              // Add recurring-specific fields
              if (isRecurring) {
                emailData.StartDate = values.recurringStartDate ? new Date(values.recurringStartDate).toLocaleDateString('en-US') : '';
                if (values.subscriptionEndType === 'NoEndDate') {
                  emailData.EndDate = 'No End Date (Ongoing)';
                } else if (values.subscriptionEndType === 'EndsOnDate') {
                  emailData.EndDate = values.recurringEndDate ? new Date(values.recurringEndDate).toLocaleDateString('en-US') : '';
                } else if (values.subscriptionEndType === 'EndsAfterTerms') {
                  const startDate = new Date((values.recurringStartDate || '') + 'T12:00:00');
                  const numTerms = parseInt(values.numberOfTerms || '12', 10);
                  const calcEndDate = new Date(startDate);
                  calcEndDate.setMonth(calcEndDate.getMonth() + numTerms);
                  emailData.EndDate = `${calcEndDate.toLocaleDateString('en-US')} (${numTerms} payments)`;
                }
              }
              
              // Use sendEmailMessage with DonationGrocery app and appropriate template
              const templateName = isRecurring ? 'RMSDonationGroceryRecurring' : 'RMSDonationGrocery';
              console.log('Sending email with template:', templateName);
              console.log('Email data:', emailData);
              const emailResp: any = await donationService.sendEmailMessage(
                emailData,
                { appName: 'DonationGrocery', templateName: templateName, orgId: 3 }
              );
              console.log('Email response:', emailResp);
              
              // Debug: Log email response to see what's returned
              console.log('=== Email Response Debug ===');
              console.log('Full emailResp:', JSON.stringify(emailResp, null, 2));
              
              // Extract receipt URL from various possible response formats
              if (typeof emailResp === 'string') {
                receiptUrl = emailResp;
                console.log('Receipt URL (string):', receiptUrl);
              } else {
                receiptUrl = (
                  emailResp?.receiptUrl || emailResp?.ReceiptUrl || emailResp?.receiptURL ||
                  emailResp?.attachment || emailResp?.Attachment ||
                  emailResp?.attachmentUrl || emailResp?.AttachmentUrl || emailResp?.attachmentURL ||
                  emailResp?.pdfUrl || emailResp?.pdfURL ||
                  emailResp?.fileUrl || emailResp?.fileURL ||
                  emailResp?.url || emailResp?.Url || emailResp?.URL ||
                  emailResp?.Response || emailResp?.response ||
                  emailResp?.data?.receiptUrl || emailResp?.data?.ReceiptUrl || emailResp?.data?.receiptURL ||
                  emailResp?.data?.attachment || emailResp?.data?.Attachment ||
                  emailResp?.data?.attachmentUrl || emailResp?.data?.AttachmentUrl || emailResp?.data?.attachmentURL ||
                  emailResp?.data?.pdfUrl || emailResp?.data?.pdfURL ||
                  emailResp?.data?.fileUrl || emailResp?.data?.fileURL ||
                  emailResp?.data?.url || emailResp?.data?.Url || emailResp?.data?.URL ||
                  emailResp?.data?.Response || emailResp?.data?.response ||
                  ''
                );
                console.log('Receipt URL (extracted):', receiptUrl);
              }
            } catch (emailError) {
              console.warn('Email send failed, continuing with success UI:', emailError);
            }
            
            console.log('Final receiptUrl:', receiptUrl);
            console.log('Will show receipt buttons:', !!receiptUrl);

            // Build receipt buttons HTML if receipt URL is available
            const receiptButtonsHtml = receiptUrl ? `
              <div style="display: flex; gap: 12px; justify-content: center; margin-top: 1rem;">
                <a href="${receiptUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-flex; align-items: center; gap: 6px; padding: 10px 20px; background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%); color: white; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 0.9rem; box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3); transition: all 200ms ease;">
                  <i class="fas fa-eye"></i> View Receipt
                </a>
                <a href="${receiptUrl}" download style="display: inline-flex; align-items: center; gap: 6px; padding: 10px 20px; background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 0.9rem; box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3); transition: all 200ms ease;">
                  <i class="fas fa-download"></i> Download Receipt
                </a>
              </div>
            ` : '';

            Swal.fire({
              icon: 'success',
              title: '🎉 Thank You!',
              html: `
                <div class="text-center">
                  <p class="text-lg font-semibold mb-3" style="color: #10b981;">Your donation has been processed successfully!</p>
                  <div style="background: #f0fdf4; border-radius: 12px; padding: 1.5rem; margin: 1rem 0;">
                    <p class="mb-2"><strong>Amount:</strong> <span style="font-size: 1.5rem; color: #10b981; font-weight: bold;">$${totalAmount.toFixed(2)}</span></p>
                    ${values.donationFrequency === 'recurring' ? `<p class="mb-2"><strong>Frequency:</strong> <span style="color: #059669;">Monthly</span></p>` : ''}
                    <p class="mb-2"><strong>Payment Method:</strong> ${values.paymentMethod === 'creditcard' ? 'Credit Card' : 'ACH'}</p>
                    ${subscriptionId ? `<p class="mb-0"><strong>Subscription ID:</strong> <span style="font-family: monospace; color: #6b7280;">${subscriptionId}</span></p>` : ''}
                    ${transactionId && !subscriptionId ? `<p class="mb-0"><strong>Transaction ID:</strong> <span style="font-family: monospace; color: #6b7280;">${transactionId}</span></p>` : ''}
                  </div>
                  ${values.donationFrequency === 'recurring' ? (() => {
                    const startDateFormatted = values.recurringStartDate ? new Date(values.recurringStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'the start date';
                    let endDateText = '';
                    if (values.subscriptionEndType === 'NoEndDate') {
                      endDateText = 'and will continue <strong>indefinitely</strong>';
                    } else if (values.subscriptionEndType === 'EndsOnDate') {
                      const endDateFormatted = values.recurringEndDate ? new Date(values.recurringEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'the end date';
                      endDateText = `until <strong>${endDateFormatted}</strong>`;
                    } else if (values.subscriptionEndType === 'EndsAfterTerms') {
                      endDateText = `for <strong>${values.numberOfTerms} monthly payments</strong>`;
                    }
                    return `
                    <div style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 1rem; margin: 1rem 0; text-align: left;">
                      <p class="mb-0" style="color: #92400e; font-size: 0.9rem;">
                        <i class="fas fa-info-circle me-1"></i>
                        <strong>Recurring Payment:</strong> The amount of <strong>$${totalAmount.toFixed(2)}</strong> will be automatically deducted every month using your selected ${values.paymentMethod === 'creditcard' ? 'Credit Card' : 'ACH'} payment method starting <strong>${startDateFormatted}</strong> ${endDateText}.
                      </p>
                    </div>
                  `;
                  })() : ''}
                  <p class="text-sm" style="color: #6b7280; margin-top: 1rem;">
                    <i class="fas fa-envelope me-1"></i>
                    A confirmation email has been sent to <strong>${values.donorEmail}</strong>
                  </p>
                  ${receiptButtonsHtml}
                  <p class="text-sm" style="color: #10b981; margin-top: 1rem;">
                    <i class="fas fa-heart me-1"></i>
                    Thank you for supporting our temple community!
                  </p>
                </div>
              `,
              confirmButtonColor: '#10b981',
              confirmButtonText: 'Done',
              width: '600px',
            }).then(() => {
              // Reset reCAPTCHA
              try {
                const w = window as any;
                if (recaptchaWidgetId !== null && w.grecaptcha) {
                  w.grecaptcha.reset(recaptchaWidgetId);
                  setRecaptchaToken('');
                }
              } catch {}
              window.location.href = '/donate-grocery';
            });
          } else {
            // Handle database save errors
            if (dbResponse?.errors) {
              const errorMessages = Object.entries(dbResponse.errors)
                .map(([field, messages]: [string, any]) => {
                  const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
                  const errorList = Array.isArray(messages) ? messages : [messages];
                  return `<strong>${fieldName}:</strong> ${errorList.join(', ')}`;
                })
                .join('<br/>');
              
              Swal.fire({
                icon: 'error',
                title: 'Database Error',
                html: `<div class="text-start">${errorMessages}</div>`,
                confirmButtonColor: '#ef4444',
              });
              setLoading(false);
              return;
            }
            throw new Error(dbResponse?.message || 'Failed to save donation record');
          }
        } catch (paymentError: any) {
          console.error('Payment error:', paymentError);
          
          // Handle API error response
          if (paymentError?.response?.data) {
            const errorData = paymentError.response.data;
            
            // Check for validation errors
            if (errorData.errors) {
              const errorMessages = Object.entries(errorData.errors)
                .map(([field, messages]: [string, any]) => {
                  const fieldName = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
                  const errorList = Array.isArray(messages) ? messages : [messages];
                  return `<div style="margin-bottom: 0.5rem;"><strong>${fieldName}:</strong> ${errorList.join(', ')}</div>`;
                })
                .join('');
              
              Swal.fire({
                icon: 'error',
                title: errorData.title || 'Validation Error',
                html: `
                  <div style="text-align: left; max-height: 400px; overflow-y: auto;">
                    ${errorMessages}
                  </div>
                  ${errorData.traceId ? `<p style="font-size: 0.75rem; color: #9ca3af; margin-top: 1rem;">Trace ID: ${errorData.traceId}</p>` : ''}
                `,
                confirmButtonColor: '#ef4444',
                width: '600px',
              });
              setLoading(false);
              return;
            }
            
            // Check for Authorize.Net specific error structure
            const authNetErrors: string[] = [];
            if (errorData.errorMessages) {
              const errors = Array.isArray(errorData.errorMessages) 
                ? errorData.errorMessages 
                : [errorData.errorMessages];
              errors.forEach((err: any) => {
                if (typeof err === 'string') {
                  authNetErrors.push(err);
                } else if (err?.message) {
                  authNetErrors.push(err.message);
                } else if (err?.errorText) {
                  authNetErrors.push(`${err.errorText} (Code: ${err.errorCode || 'N/A'})`);
                }
              });
            }
            
            // Check for ARB subscription response errors (for recurring payments)
            if (errorData.aRBCreateSubscriptionResponse?.messages?.message) {
              const arbMessages = Array.isArray(errorData.aRBCreateSubscriptionResponse.messages.message)
                ? errorData.aRBCreateSubscriptionResponse.messages.message
                : [errorData.aRBCreateSubscriptionResponse.messages.message];
              arbMessages.forEach((msg: any) => {
                const msgText = msg?.text || msg?.description || '';
                const msgCode = msg?.code || '';
                if (msgText) {
                  authNetErrors.push(msgCode ? `${msgText} (Code: ${msgCode})` : msgText);
                }
              });
            }
            
            // Check for messages array with error details
            if (errorData.messages?.message) {
              const messages = Array.isArray(errorData.messages.message)
                ? errorData.messages.message
                : [errorData.messages.message];
              messages.forEach((msg: any) => {
                const msgText = msg?.text || msg?.description || '';
                const msgCode = msg?.code || '';
                if (msgText && msgCode && msgCode.startsWith('E')) {
                  // Only add if it's an error code (starts with E)
                  authNetErrors.push(msgCode ? `${msgText} (Code: ${msgCode})` : msgText);
                }
              });
            }
            
            // Check for transaction response errors
            if (errorData.createTransactionResponse?.transactionResponse?.errors) {
              const errors = errorData.createTransactionResponse.transactionResponse.errors;
              const errorArray = Array.isArray(errors) ? errors : [errors];
              errorArray.forEach((err: any) => {
                if (err?.errorText) {
                  authNetErrors.push(`${err.errorText} (Code: ${err.errorCode || 'N/A'})`);
                }
              });
            }
            
            // Display Authorize.Net errors if found
            if (authNetErrors.length > 0) {
              Swal.fire({
                icon: 'error',
                title: 'Payment Failed',
                html: `
                  <div style="text-align: left;">
                    <p style="margin-bottom: 1rem;">${authNetErrors.join('<br/>')}</p>
                    ${errorData.createTransactionResponse?.transactionResponse?.transId 
                      ? `<p style="font-size: 0.875rem; color: #6b7280;">Transaction ID: ${errorData.createTransactionResponse.transactionResponse.transId}</p>` 
                      : ''}
                  </div>
                `,
                confirmButtonColor: '#ef4444',
                width: '500px',
              });
              setLoading(false);
              return;
            }
            
            // Handle other API errors
            Swal.fire({
              icon: 'error',
              title: errorData.title || 'Payment Error',
              text: errorData.message || errorData.detail || 'Payment processing failed. Please try again.',
              confirmButtonColor: '#ef4444',
            });
            setLoading(false);
            return;
          }
          
          throw new Error(paymentError?.message || 'Payment processing failed. Please try again.');
        }
      }
    } catch (error: any) {
      console.error('Error submitting donation:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'An error occurred. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const getInitialValues = (): GroceryDonationFormData & { billingZipCode?: string } => ({
    donorFirstName: '',
    donorLastName: '',
    donorEmail: '',
    donorPhone: '',
    donorAddress: '',
    donorCity: '',
    donorState: '',
    donorPostalCode: '',
    donorCountry: 'United States',
    billingZipCode: '',
    donationType: 'physical',
    dropOffDate: '',
    dropOffTime: '',
    paymentMethod: 'creditcard',
    achAccountType: 'Checking',
    donationFrequency: 'onetime',
    recurringFrequency: 'monthly',
    recurringStartDate: todayStr,
    recurringEndDate: oneYearLaterStr,
    subscriptionEndType: 'NoEndDate',
    numberOfTerms: '12',
    groceryItems: groceryItems.map((item: any) => ({
      itemId: item.id,
      itemName: item.name,
      quantity: 0,
      unit: 'qty',
      estimatedPrice: item.estimatedPrice,
    })),
    totalAmount: 0,
    specialInstructions: '',
  });

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #f0f9ff 100%)' }}>
      <style>{`
        .yk-topbar {
          width: 100%;
          background: #fbbf24;
          color: #ffffff;
          font-size: 14px;
          line-height: 1.3;
          font-weight: 600;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
        }

        .yk-topbar .inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 10px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .yk-topbar .info {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .yk-topbar .info a {
          color: #ffffff;
          text-decoration: none;
          opacity: 0.95;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
          font-weight: 700;
        }

        .yk-topbar .info a:hover {
          opacity: 1;
          text-decoration: underline;
        }

        .yk-topbar .social-links {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 10px;
          justify-content: flex-end;
          flex-wrap: wrap;
        }

        .yk-topbar .social-links a {
          color: #ffffff;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 999px;
          background: rgba(0, 0, 0, 0.22);
          border: 1px solid rgba(255, 255, 255, 0.25);
        }

        .yk-topbar .social-links a:hover {
          background: rgba(0, 0, 0, 0.3);
        }

        .yk-hero-logo {
          position: absolute;
          left: 20px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 11;
          display: flex;
          align-items: center;
        }

        .yk-hero-logo img {
          height: 58px;
          width: auto;
          display: block;
          filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.35));
        }

        @media (max-width: 480px) {
          .yk-hero-logo img {
            height: 46px;
          }
        }

        .yk-topbar .top-left,
        .yk-topbar .top-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        @media (max-width: 600px) {
          .yk-topbar .inner {
            justify-content: center;
            text-align: center;
          }

          .yk-topbar .top-right {
            justify-content: center;
          }

          .yk-topbar .info {
            justify-content: center;
          }

          .yk-topbar .social-links {
            justify-content: center;
          }
        }

        @media (max-width: 576px) {
          .yk-topbar {
            display: none;
          }

          .yk-hero-center {
            padding-left: 78px;
            padding-right: 78px;
            width: 100%;
          }

          .yk-hero-center .fas.fa-shopping-bag {
            display: none;
          }

          .yk-hero-center > div {
            gap: 0.35rem !important;
          }
        }

        .gradient-header {
          background: rgba(255, 255, 255, 0.96);
          color: #111827;
          padding: 1.5rem 1rem;
          text-align: center;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
          animation: slideDown 0.6s ease-out;
        }

        .card-hover {
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          border: 2px solid rgba(14, 165, 233, 0.2);
          background: white;
        }

        .card-hover:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(14, 165, 233, 0.2);
          border-color: rgba(14, 165, 233, 0.4);
        }

        .item-card {
          background: #ffffff;
          border: 2px solid rgba(17, 24, 39, 0.08);
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          position: relative;
          overflow: hidden;
          border-radius: 20px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04), inset 0 -2px 0 rgba(0, 0, 0, 0.05);
          backdrop-filter: blur(10px);
        }

        .item-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
          transition: left 0.5s;
        }

        .item-card::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(15, 23, 42, 0.06) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .item-card:hover::before {
          left: 100%;
        }

        .item-card:hover::after {
          opacity: 1;
        }

        .item-card:hover {
          transform: translateY(-10px) scale(1.05);
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08), 0 8px 20px rgba(15, 23, 42, 0.06), inset 0 -3px 0 rgba(0, 0, 0, 0.06);
          border-color: rgba(15, 23, 42, 0.16);
        }

        .item-card.selected {
          background: rgba(255, 251, 235, 0.92);
          border: 1px solid rgba(251, 191, 36, 0.22);
          box-shadow: 0 18px 45px rgba(251, 191, 36, 0.14), 0 6px 18px rgba(15, 23, 42, 0.05);
          outline: 2px solid rgba(251, 191, 36, 0.18);
          outline-offset: 2px;
          backdrop-filter: blur(10px);
          transform: translateY(-12px) scale(1.06);
          animation: none;
        }

        .item-card.selected::before {
          left: -120%;
          animation: yk-sheen 1.6s ease-in-out infinite;
        }

        .item-card.selected::after {
          opacity: 1;
          background: radial-gradient(circle, rgba(251, 191, 36, 0.10) 0%, transparent 70%);
        }

        .item-card.selected .item-icon,
        .item-card.selected .item-image {
          position: relative;
        }

        .item-card.selected .item-icon::after,
        .item-card.selected .item-image::after {
          content: '✓';
          position: absolute;
          top: -10px;
          right: -10px;
          width: 24px;
          height: 24px;
          border-radius: 999px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.95);
          border: 2px solid rgba(251, 191, 36, 0.5);
          color: rgba(15, 23, 42, 0.95);
          font-size: 0.9rem;
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
          animation: none !important;
          transition: none !important;
          z-index: 10;
        }

        @keyframes yk-sheen {
          0% { left: -120%; }
          60% { left: 120%; }
          100% { left: 120%; }
        }

        @keyframes yk-pop-in {
          0% { transform: scale(0.92); opacity: 0; }
          60% { transform: scale(1.06); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }

        .yk-next-pop {
          animation: yk-pop-in 0.35s ease-out;
        }

        @keyframes yk-attention {
          0%, 100% {
            transform: translateY(0) scale(1);
            box-shadow: 0 4px 12px rgba(251, 191, 36, 0.22);
            filter: saturate(1);
          }
          50% {
            transform: translateY(-1px) scale(1.06);
            box-shadow: 0 10px 28px rgba(251, 191, 36, 0.45);
            filter: saturate(1.15);
          }
        }

        .yk-attention {
          animation: yk-attention 0.95s ease-in-out infinite;
        }

        .item-card.selected h6,
        .item-card.selected p,
        .item-card.selected span {
          color: #0f172a !important;
          text-shadow: none !important;
          font-weight: 800 !important;
          letter-spacing: 0.005em;
        }

        .item-card h6 {
          color: #0f172a !important;
          text-shadow: none !important;
        }

        .item-card:not(.selected) h6 {
          font-weight: 600 !important;
        }

        .item-card .d-flex.justify-content-between.align-items-center span {
          color: rgba(15, 23, 42, 0.72) !important;
        }

        .item-card .d-flex.justify-content-between.align-items-center span.fw-bold {
          color: #0f172a !important;
        }

        .item-card:not(.selected) .d-flex.justify-content-between.align-items-center span.fw-bold {
          font-weight: 600 !important;
        }

        .item-card.selected .subtotal-badge,
        .item-card.selected .subtotal-badge * {
          color: #0f172a !important;
          font-weight: 800;
        }

        .item-card.selected .item-icon {
          animation: none !important;
          filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 22px 55px rgba(15, 23, 42, 0.10), 0 8px 22px rgba(15, 23, 42, 0.07), inset 0 -3px 0 rgba(0, 0, 0, 0.06);
          }
          50% {
            box-shadow: 0 26px 65px rgba(15, 23, 42, 0.12), 0 10px 26px rgba(15, 23, 42, 0.08), inset 0 -4px 0 rgba(0, 0, 0, 0.07);
          }
        }

        @keyframes rotate-pulse {
          0%, 100% {
            transform: scale(1) rotate(0deg);
          }
          50% {
            transform: scale(1.1) rotate(5deg);
          }
        }

        @keyframes spinPulse {
          0%, 100% {
            transform: scale(1) rotate(0deg);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.05) rotate(180deg);
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }

        .item-icon {
          font-size: 3rem;
          margin-bottom: 0.5rem;
          display: block;
          width: 80px;
          height: 80px;
          line-height: 80px;
          margin: 0 auto 0.5rem;
        }

        .item-image {
          width: 80px !important;
          height: 80px !important;
          max-width: 80px !important;
          object-fit: cover;
          border-radius: 8px;
          margin: 0 auto 0.5rem;
          display: block;
        }

        .payment-fields-container {
          background: rgba(248, 250, 252, 0.92);
          border: 1px solid rgba(15, 23, 42, 0.12);
          border-radius: 16px;
          padding: 2rem;
          margin-top: 1.5rem;
          box-shadow: 0 18px 50px rgba(15, 23, 42, 0.08), 0 6px 18px rgba(15, 23, 42, 0.05);
          backdrop-filter: blur(10px);
          animation: slideDown 0.4s ease-out;
        }

        .payment-fields-container .alert.alert-info {
          background: rgba(255, 255, 255, 0.92) !important;
          border: 1px solid rgba(15, 23, 42, 0.12) !important;
          color: #0f172a !important;
          border-radius: 14px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
        }

        .payment-fields-container .alert.alert-info h6,
        .payment-fields-container .alert.alert-info p {
          color: #0f172a !important;
        }

        .payment-fields-container .alert.alert-info i {
          color: rgba(251, 191, 36, 0.9);
        }

        .summary-card {
          background: #ffffff;
          color: #111827;
          border: 1px solid rgba(15, 23, 42, 0.10);
          box-shadow: 0 18px 50px rgba(15, 23, 42, 0.10), 0 6px 18px rgba(15, 23, 42, 0.06);
          backdrop-filter: blur(10px);
          animation: slideUp 0.8s ease-out;
        }

        .summary-card::-webkit-scrollbar {
          width: 8px;
        }

        .summary-card::-webkit-scrollbar-thumb {
          background: rgba(15, 23, 42, 0.18);
          border-radius: 999px;
        }

        .summary-card .row.g-3 > .col-12 > div {
          background: #f8fafc !important;
          border: 1px solid rgba(15, 23, 42, 0.12) !important;
          box-shadow: 0 1px 0 rgba(15, 23, 42, 0.04) !important;
        }

        .yk-summary-items {
          max-height: 228px;
          overflow-y: auto;
          margin-bottom: 0.75rem;
          padding-right: 6px;
        }

        .yk-summary-items::-webkit-scrollbar {
          width: 8px;
        }

        .yk-summary-items::-webkit-scrollbar-thumb {
          background: rgba(15, 23, 42, 0.18);
          border-radius: 999px;
        }

        .summary-card h5,
        .summary-card p,
        .summary-card span,
        .summary-card h2 {
          color: #0f172a !important;
        }

        .summary-card h5 {
          font-size: 1.2rem !important;
          font-weight: 800 !important;
          letter-spacing: -0.01em;
        }

        .summary-card p {
          font-size: 1.02rem !important;
          font-weight: 700 !important;
        }

        .summary-card span {
          font-size: 1rem !important;
          font-weight: 700 !important;
        }

        .summary-card h2 {
          font-weight: 900 !important;
          letter-spacing: -0.02em;
        }

        .mobile-cart-panel {
          font-size: 1.02rem;
        }

        .mobile-cart-panel h5,
        .mobile-cart-panel h6 {
          color: #0f172a !important;
          font-weight: 800 !important;
          letter-spacing: -0.01em;
        }

        .mobile-cart-panel p,
        .mobile-cart-panel span,
        .mobile-cart-panel div {
          color: #0f172a !important;
        }

        .mobile-cart-panel .text-muted {
          color: rgba(15, 23, 42, 0.68) !important;
        }

        .btn-donate {
          background: linear-gradient(135deg, #fbbf24 0%, #38bdf8 100%);
          border: none;
          color: white;
          font-weight: 700;
          padding: 0.75rem 2rem;
          transition: all 0.3s ease;
          box-shadow: 0 10px 25px rgba(251, 191, 36, 0.3);
        }

        .btn-donate:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 15px 35px rgba(251, 191, 36, 0.5);
          color: white;
          background: linear-gradient(135deg, #f59e0b 0%, #0ea5e9 100%);
        }

        .btn-donate:not(:disabled) {
          animation: pulse 2s ease-in-out infinite;
        }

        .btn-donate-3d {
          background: linear-gradient(145deg, #fbbf24 0%, #38bdf8 100%);
          border: none;
          color: white;
          font-weight: 700;
          font-size: 1.1rem;
          padding: 1rem 2.5rem;
          border-radius: 50px;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 8px 20px rgba(251, 191, 36, 0.4), 0 4px 10px rgba(56, 189, 248, 0.3), inset 0 -3px 0 rgba(0, 0, 0, 0.2);
          position: relative;
          overflow: hidden;
        }

        .btn-donate-3d::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          transition: left 0.5s;
        }

        .btn-donate-3d:hover::before {
          left: 100%;
        }

        .btn-donate-3d:hover {
          transform: translateY(-5px) scale(1.05);
          box-shadow: 0 15px 35px rgba(251, 191, 36, 0.5), 0 8px 20px rgba(56, 189, 248, 0.4), inset 0 -3px 0 rgba(0, 0, 0, 0.3);
          background: linear-gradient(145deg, #f59e0b 0%, #0ea5e9 100%);
        }

        .btn-donate-3d:active {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 5px 15px rgba(251, 191, 36, 0.4), 0 3px 10px rgba(56, 189, 248, 0.3), inset 0 -2px 0 rgba(0, 0, 0, 0.2);
        }

        .btn-donate-3d:not(:disabled) {
          animation: glow 2s ease-in-out infinite;
        }

        @keyframes glow {
          0%, 100% {
            box-shadow: 0 8px 20px rgba(251, 191, 36, 0.4), 0 4px 10px rgba(56, 189, 248, 0.3), inset 0 -3px 0 rgba(0, 0, 0, 0.2);
          }
          50% {
            box-shadow: 0 12px 30px rgba(251, 191, 36, 0.6), 0 6px 15px rgba(56, 189, 248, 0.5), 0 0 20px rgba(251, 191, 36, 0.3), inset 0 -3px 0 rgba(0, 0, 0, 0.2);
          }
        }

        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 10px 25px rgba(251, 191, 36, 0.3);
          }
          50% {
            box-shadow: 0 15px 40px rgba(251, 191, 36, 0.7), 0 0 30px rgba(251, 191, 36, 0.5);
            transform: scale(1.02);
          }
        }

        .form-control:focus {
          border-color: #0ea5e9;
          box-shadow: 0 0 0 0.2rem rgba(14, 165, 233, 0.25);
        }

        .form-control.is-invalid,
        .form-select.is-invalid {
          border: 2px solid #ef4444 !important;
          box-shadow: 0 0 0 0.1rem rgba(239, 68, 68, 0.15) !important;
        }

        .form-control.is-invalid:focus,
        .form-select.is-invalid:focus {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 0.2rem rgba(239, 68, 68, 0.25) !important;
        }

        .form-select:focus {
          border-color: #0ea5e9;
          box-shadow: 0 0 0 0.2rem rgba(14, 165, 233, 0.25);
        }

        .icon-box {
          width: 55px;
          height: 55px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          background: linear-gradient(135deg, #fbbf24 0%, #38bdf8 100%);
          color: white;
          font-size: 1.5rem;
          animation: float 3s ease-in-out infinite;
          box-shadow: 0 8px 20px rgba(251, 191, 36, 0.3);
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .quantity-input {
          font-weight: 700;
          text-align: center;
          border: 2px solid rgba(14, 165, 233, 0.3);
          transition: all 0.3s ease;
        }

        .quantity-input:focus {
          border-color: #0ea5e9;
          box-shadow: 0 0 0 0.2rem rgba(14, 165, 233, 0.25);
        }

        .subtotal-badge {
          background: rgba(255, 251, 235, 0.92);
          color: #0f172a;
          border: 1px solid rgba(251, 191, 36, 0.22);
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 700;
          animation: slideUp 0.3s ease-out;
          box-shadow: 0 12px 26px rgba(251, 191, 36, 0.12);
          backdrop-filter: blur(10px);
        }

        .empty-summary {
          background: white;
          border: 2px dashed rgba(148, 163, 184, 0.6);
          transition: all 0.3s ease;
        }

        .empty-summary i,
        .empty-summary h5 {
          color: #64748b !important;
        }

        textarea.form-control[name="specialInstructions"] {
          background: #ffffff !important;
          color: #0f172a !important;
          border: 1px solid rgba(15, 23, 42, 0.18) !important;
          box-shadow: none !important;
        }

        textarea.form-control[name="specialInstructions"]::placeholder {
          color: rgba(15, 23, 42, 0.5) !important;
        }

        textarea.form-control[name="specialInstructions"]:focus {
          background: #ffffff !important;
          color: #0f172a !important;
          border-color: rgba(59, 130, 246, 0.45) !important;
          box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.18) !important;
        }

        .empty-summary:hover {
          border-color: rgba(59, 130, 246, 0.35);
          transform: scale(1.02);
        }

        .donation-type-tile {
          padding: 0.875rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .donation-type-tile:hover {
          border-color: #cbd5e1;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
        }

        .donation-type-tile.active {
          border-color: #3b82f6;
          background: #eff6ff;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
        }

        .donation-type-tile .icon {
          font-size: 2rem;
          display: block;
          margin-bottom: 0.5rem;
        }

        .donation-type-tile h5 {
          font-size: 0.9rem;
          margin-bottom: 0.35rem;
        }

        .donation-type-tile p {
          font-size: 0.75rem;
          margin-bottom: 0;
        }

        @media (max-width: 768px) {
          .donation-type-tile {
            padding: 0.75rem 0.875rem;
          }
          .donation-type-tile .icon {
            font-size: 1.75rem;
            margin-bottom: 0.4rem;
          }

          .donation-type-tile h5 {
            font-size: 0.85rem;
            margin-bottom: 0.3rem;
          }

          .donation-type-tile p {
            font-size: 0.75rem;
            margin-bottom: 0;
          }
        }

        .payment-method-tile {
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .payment-method-tile:hover {
          border-color: #cbd5e1;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
        }

        .payment-method-tile.active {
          border-color: #3b82f6;
          background: #eff6ff;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
        }

        .payment-method-tile .icon {
          font-size: 1.3rem;
          margin-bottom: 0.25rem;
        }

        .payment-method-tile .fw-bold {
          font-size: 0.85rem;
          font-weight: 600;
          color: #1e293b;
        }




        @media (max-width: 768px) {
          .payment-method-tile {
            padding: 0.5rem 0.65rem;
          }
          .payment-method-tile .icon {
            font-size: 1.25rem;
            margin-bottom: 0.22rem;
          }
          .payment-method-tile .fw-bold {
            font-size: 0.82rem;
          }
        }

        /* Validation error highlighting */
        .form-control.is-invalid,
        .form-select.is-invalid {
          border-color: #dc3545 !important;
          border-width: 2px !important;
          background-color: #fff5f5 !important;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.15) !important;
        }

        .form-control.is-invalid:focus,
        .form-select.is-invalid:focus {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25) !important;
        }

        .selected-items-list {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          padding: 1rem;
          max-height: 300px;
          overflow-y: auto;
        }

        .selected-item-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          margin-bottom: 0.5rem;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .selected-item-row:hover {
          background: rgba(255, 255, 255, 0.4);
          transform: translateX(5px);
        }

        .quantity-selector {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          position: relative;
          z-index: 10;
        }

        .quantity-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 2px solid rgba(14, 165, 233, 0.3);
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          font-weight: bold;
          font-size: 1.2rem;
          color: #0ea5e9;
          position: relative;
          z-index: 10;
        }

        .quantity-btn:hover {
          background: #0ea5e9;
          color: white;
          border-color: #0ea5e9;
          transform: scale(1.1);
        }

        .quantity-btn:active {
          transform: scale(0.95);
        }

        .quantity-btn:active {
          transform: scale(0.95);
        }

        .quantity-display {
          width: 50px;
          text-align: center;
          font-weight: 700;
          font-size: 1.1rem;
          border: 2px solid rgba(14, 165, 233, 0.3);
          border-radius: 8px;
          padding: 0.25rem;
          background: white;
        }

        .nav-btn-small {
          padding: 0.5rem 1.25rem;
          font-size: 0.875rem;
          border-radius: 25px;
          font-weight: 600;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          border: none;
          position: relative;
          overflow: hidden;
        }

        .btn-primary.nav-btn-small {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3), inset 0 -2px 0 rgba(0, 0, 0, 0.15);
        }

        .btn-primary.nav-btn-small::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          transition: left 0.5s;
        }

        .btn-primary.nav-btn-small:hover::before {
          left: 100%;
        }

        .btn-primary.nav-btn-small:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4), inset 0 -2px 0 rgba(0, 0, 0, 0.2);
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
        }

        .btn-primary.nav-btn-small:active {
          transform: translateY(-1px) scale(1.02);
          box-shadow: 0 3px 10px rgba(59, 130, 246, 0.3), inset 0 -1px 0 rgba(0, 0, 0, 0.15);
        }

        .btn-outline-secondary.nav-btn-small {
          background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
          border: 2px solid #d1d5db;
          color: #374151;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
        }

        .btn-outline-secondary.nav-btn-small::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
          transition: left 0.5s;
        }

        .btn-outline-secondary.nav-btn-small:hover::before {
          left: 100%;
        }

        .btn-outline-secondary.nav-btn-small:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12), inset 0 -2px 0 rgba(0, 0, 0, 0.15);
          background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
          border-color: #9ca3af;
          color: #1f2937;
        }

        .btn-outline-secondary.nav-btn-small:active {
          transform: translateY(-1px) scale(1.02);
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1);
        }

        @media (max-width: 768px) {
          .nav-btn-small {
            padding: 0.45rem 1rem;
            font-size: 0.8rem;
          }
        }

        .mobile-cart-icon {
          position: absolute;
          top: 50%;
          right: 20px;
          transform: translateY(-50%);
          z-index: 10;
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
          color: white;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: none;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4);
          transition: all 0.3s ease;
        }

        .mobile-cart-icon:hover {
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 8px 20px rgba(251, 191, 36, 0.6);
        }

        .mobile-cart-icon .badge {
          position: absolute;
          top: -5px;
          right: -5px;
          background: #ef4444;
          color: white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: bold;
          border: 2px solid white;
        }

        .mobile-cart-panel {
          position: fixed;
          top: 0;
          right: -100%;
          width: 90%;
          max-width: 400px;
          height: 100vh;
          background: white;
          z-index: 9999;
          transition: right 0.3s ease;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.2);
          overflow-y: auto;
        }

        .mobile-cart-panel.open {
          right: 0;
        }

        .mobile-cart-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          background: rgba(0, 0, 0, 0.5);
          z-index: 9998;
          display: none;
        }

        .mobile-cart-overlay.open {
          display: block;
        }

        @media (max-width: 991px) {
          .mobile-cart-icon {
            display: flex;
          }
          
          .donation-summary-sidebar {
            display: none !important;
          }
        }

        /* reCAPTCHA mobile responsive fixes */
        @media (max-width: 480px) {
          #recaptcha-container {
            transform: scale(0.85) !important;
            transform-origin: 0 0 !important;
          }
        }

        @media (max-width: 360px) {
          #recaptcha-container {
            transform: scale(0.75) !important;
            transform-origin: 0 0 !important;
          }
        }

        #recaptcha-container > div {
          width: 304px !important;
          height: 78px !important;
        }

        /* Ensure reCAPTCHA iframe is clickable */
        #recaptcha-container iframe {
          pointer-events: auto !important;
          touch-action: auto !important;
        }

        /* Force SweetAlert to use proper viewport positioning */
        html.swal2-shown,
        body.swal2-shown {
          overflow: hidden !important;
          height: 100% !important;
        }

      `}</style>

      <div className="yk-topbar">
        <div className="inner clearfix">
          <div className="top-left clearfix">
            <ul className="info clearfix">
              <li>
                <a className="" href="tel:678-920-6669">
                  <span className="icon fa fa-phone-alt"></span> &nbsp;678-920-6669
                </a>
              </li>
              <li>
                <a className="" href="mailto:info@yugalkunj.org">
                  <span className="icon fa fa-envelope-open"></span> Email: &nbsp;info@yugalkunj.org
                </a>
              </li>
            </ul>
          </div>
          <div className="top-right  temple-time clearfix">
            <ul className="info clearfix">
              <li>
                <a className="" href="https://www.facebook.com/yugalkunj/" target="_blank" rel="noreferrer">
                  <span className="icon fa fa-clock"></span> Sunday - Sunday 7:00 AM&nbsp;-&nbsp;8:30&nbsp;PM
                </a>
              </li>
            </ul>
            <ul className="social-links clearfix">
              <li><a className="" href="https://www.facebook.com/yugalkunj/" target="_blank" rel="noreferrer"><span className="fab fa-facebook-f"></span></a></li>
              <li><a className="" href="https://twitter.com/search?q=yugalkunj" target="_blank" rel="noreferrer"><span className="fab fa-twitter"></span></a></li>
              <li><a className="" href="https://www.yelp.com/biz/yugal-kunj-duluth-2" target="_blank" rel="noreferrer"><span className="fab fa-yelp"></span></a></li>
              <li><a className="" href="https://www.youtube.com/channel/UC-3Ydnp9Z5ER1jz7_gZVDQA" target="_blank" rel="noreferrer"><span className="fab fa-youtube"></span></a></li>
              <li><a className="" href="https://www.instagram.com/yugal_kunj/" target="_blank" rel="noreferrer"><span className="fab fa-instagram"></span></a></li>
              <li><a className="" href="https://video.search.yahoo.com/search/video;_ylt=AwrC1DGkf51cEi8A_gzQtDMD;_ylu=X3oDMTB0N2Noc21lBGNvbG8DYmYxBHBvcwMxBHZ0aWQDBHNlYwNwaXZz?p=yugal+kunj+duluth+ga&amp;fr2=piv-web&amp;fr=yfp-t-s" target="_blank" rel="noreferrer"><span className="fab fa-yahoo"></span></a></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Loading Indicator */}
      {itemsLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border text-warning" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: '1rem', color: '#6b7280', fontSize: '1.1rem' }}>Loading grocery items...</p>
          </div>
        </div>
      )}

      {/* Hero Header */}
      <div className="gradient-header" style={{ 
        position: 'relative',
        background: 'rgba(255, 255, 255, 0.96)',
        backdropFilter: 'blur(15px)',
        padding: '0.75rem 0',
        boxShadow: '0 2px 15px rgba(0, 0, 0, 0.10), 0 1px 3px rgba(0, 0, 0, 0.06)',
        borderBottom: '1px solid rgba(17, 24, 39, 0.10)'
      }}>
        <div className="yk-hero-logo">
          <img src="https://stssevastorage.blob.core.windows.net/ykwebsite/logo.png" alt="Yugal Kunj" />
        </div>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }} className="yk-hero-center">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-shopping-bag" style={{ 
                fontSize: '1.4rem',
                color: '#111827',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.18))'
              }}></i>
              <div>
                <h1 style={{
                  fontSize: 'clamp(0.9rem, 3.4vw, 1.1rem)',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: '#111827',
                  textShadow: 'none',
                  marginBottom: '0.15rem'
                }}>Donate Groceries</h1>
                <p style={{ 
                  fontSize: 'clamp(0.58rem, 2.6vw, 0.68rem)', 
                  marginBottom: 0,
                  color: 'rgba(17, 24, 39, 0.75)',
                  fontWeight: '400',
                  letterSpacing: '0.5px',
                  textShadow: 'none'
                }}>Help serve our temple community</p>
              </div>
            </div>
          </div>
        </div>

      {/* Mobile Cart Icon */}
      {(() => {
        if (!formValues) return null;
        const selectedItems = formValues.groceryItems.filter((item: any) => Number(item.quantity) > 0);
        
        return (
          <>
            {/* Mobile Cart Icon - Centered in Header */}
            <div className="mobile-cart-icon" onClick={() => setShowMobileCart(true)}>
              <i className="fas fa-shopping-cart" style={{ fontSize: '1.3rem' }}></i>
              {selectedItems.length > 0 && (
                <div className="badge">{selectedItems.length}</div>
              )}
            </div>
          </>
        );
      })()}
      </div>

      {/* Mobile Cart Overlay and Panel - Moved outside header for proper z-index */}
      {(() => {
        if (!formValues) return null;
        const selectedItems = formValues.groceryItems.filter((item: any) => Number(item.quantity) > 0);
        const totalAmount = selectedItems.reduce((sum: number, item: any) => sum + (Number(item.quantity) * item.estimatedPrice), 0);
        
        return (
          <>
            {/* Mobile Cart Overlay */}
            <div 
              className={`mobile-cart-overlay ${showMobileCart ? 'open' : ''}`}
              onClick={() => setShowMobileCart(false)}
            ></div>

            {/* Mobile Cart Panel - Full Summary */}
            <div className={`mobile-cart-panel ${showMobileCart ? 'open' : ''}`}>
                <div style={{ 
                  background: '#ffffff',
                  padding: '1.5rem',
                  minHeight: '100vh'
                }}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-check-circle me-2" style={{ fontSize: '1.1rem', color: '#1e293b' }}></i>
                      <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Donation Summary</h5>
                    </div>
                    <button 
                      className="btn btn-sm rounded-circle"
                      style={{ width: '36px', height: '36px', padding: 0, background: 'rgba(255,255,255,0.3)', border: 'none' }}
                      onClick={() => setShowMobileCart(false)}
                    >
                      <i className="fas fa-times" style={{ color: '#1e293b' }}></i>
                    </button>
                  </div>

                  {selectedItems.length > 0 ? (
                    <div>
                      {/* Selected Items */}
                      <div className="mb-3">
                        <p className="mb-2 fw-semibold" style={{ fontSize: '0.85rem', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Selected Items</p>
                        <div className="yk-summary-items">
                          {selectedItems.map((item: any, idx: number) => {
                            const groceryItem = groceryItems.find((g: any) => g.id === item.itemId);
                            return (
                              <div key={idx} style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                padding: '0.5rem 0.75rem', 
                                marginBottom: '0.4rem', 
                                background: 'rgba(255, 255, 255, 0.25)', 
                                borderRadius: '8px'
                              }}>
                                <div className="d-flex align-items-center gap-2">
                                  <span style={{ fontSize: '1.2rem' }}>{groceryItem?.icon || '🛒'}</span>
                                  <div>
                                    <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.2' }}>{item.itemName}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#475569' }}>Qty: {item.quantity}</div>
                                  </div>
                                </div>
                                {formValues.donationType === 'online' && (
                                  <div className="fw-bold" style={{ fontSize: '0.85rem', color: '#1e293b' }}>${(Number(item.quantity) * item.estimatedPrice).toFixed(2)}</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ background: 'rgba(255, 255, 255, 0.35)', borderRadius: '10px', padding: '0.75rem 1rem' }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <span style={{ fontSize: '0.8rem', color: '#1e293b', fontWeight: '600' }}>Total Items</span>
                            <span className="fw-bold" style={{ color: '#1e293b', fontSize: '1.5rem' }}>{selectedItems.reduce((sum: number, item: any) => sum + Number(item.quantity), 0)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Payment Details */}
                      <div className="mb-3">
                        <div style={{ background: 'rgba(255, 255, 255, 0.25)', borderRadius: '10px', padding: '0.75rem 1rem' }}>
                          <p className="mb-2 fw-semibold" style={{ fontSize: '0.85rem', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment Details</p>
                          {formValues.donationType === 'online' ? (
                            <div>
                              <div className="d-flex align-items-center mb-2">
                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px', fontSize: '1.1rem' }}>
                                  {formValues.donationFrequency === 'recurring' ? '🔄' : '🎁'}
                                </div>
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.2' }}>
                                    {formValues.donationFrequency === 'recurring' ? 'Recurring' : 'One-Time'}
                                  </div>
                                  {formValues.donationFrequency === 'recurring' && formValues.recurringFrequency && (
                                    <div style={{ fontSize: '0.7rem', color: '#475569' }}>
                                      {formValues.recurringFrequency.charAt(0).toUpperCase() + formValues.recurringFrequency.slice(1)}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="d-flex align-items-center">
                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px', fontSize: '1.1rem' }}>
                                  {formValues.paymentMethod === 'creditcard' ? '💳' : '🏦'}
                                </div>
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.2' }}>
                                    {formValues.paymentMethod === 'creditcard' ? 'Credit Card' : 'ACH Transfer'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="d-flex align-items-center mb-2">
                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px', fontSize: '1.1rem' }}>
                                  📦
                                </div>
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.2' }}>Physical Drop-Off</div>
                                </div>
                              </div>
                              {formValues.dropOffDate && formValues.dropOffTime && (
                                <div className="d-flex align-items-center mt-2">
                                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px', fontSize: '1rem' }}>
                                    📅
                                  </div>
                                  <div>
                                    <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.2' }}>{new Date(formValues.dropOffDate).toLocaleDateString()}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#475569' }}>{formValues.dropOffTime}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Total Amount */}
                      {formValues.donationType === 'online' && (
                        <div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.35)', borderRadius: '10px', padding: '1rem', border: '2px solid rgba(255,255,255,0.5)' }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <span style={{ fontSize: '0.8rem', color: '#1e293b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Amount</span>
                              <h2 className="mb-0 fw-bold" style={{ fontSize: '1.8rem', color: '#1e293b' }}>${totalAmount.toFixed(2)}</h2>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center" style={{ padding: '3rem 1.5rem' }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        margin: '0 auto 1.5rem',
                        borderRadius: '50%',
                        background: 'rgba(251, 191, 36, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="fas fa-shopping-cart" style={{ 
                          fontSize: '2.5rem', 
                          color: 'rgba(251, 191, 36, 0.6)'
                        }}></i>
                      </div>
                      <h6 style={{ 
                        color: '#1e293b', 
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        fontSize: '1rem'
                      }}>Your Cart is Empty</h6>
                      <p style={{ 
                        color: '#64748b',
                        fontSize: '0.85rem',
                        marginBottom: '1.5rem',
                        lineHeight: '1.5'
                      }}>Start adding items to your donation cart to see them here</p>
                      <button
                        onClick={() => setShowMobileCart(false)}
                        style={{
                          background: 'linear-gradient(145deg, #fbbf24 0%, #38bdf8 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '0.65rem 1.5rem',
                          borderRadius: '25px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(251, 191, 36, 0.4)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.3)';
                        }}
                      >
                        <i className="fas fa-arrow-left me-2"></i>
                        Continue Shopping
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          );
        })()}

      {/* Stepper */}
      <div className="container mt-4">
        <div className="card shadow-sm border-0 mb-4" style={{ background: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(10px)' }}>
          <div className="card-body p-3">
            <div className="d-flex justify-content-between align-items-center position-relative">
              {/* Progress Line */}
              <div 
                className="position-absolute top-50 start-0 translate-middle-y" 
                style={{ height: '2px', width: '100%', zIndex: 0, background: 'rgba(209, 213, 219, 0.3)' }}
              ></div>
              <div 
                className="position-absolute top-50 start-0 translate-middle-y" 
                style={{ 
                  height: '2px', 
                  width: `${((currentStep - 1) / 2) * 100}%`, 
                  background: 'linear-gradient(90deg, #fbbf24 0%, #38bdf8 100%)',
                  zIndex: 0,
                  transition: 'width 0.4s ease',
                  boxShadow: '0 0 8px rgba(251, 191, 36, 0.5)'
                }}
              ></div>

              {/* Step 1 */}
              <div className="text-center position-relative" style={{ zIndex: 1, flex: 1 }}>
                <div 
                  className={`rounded-circle d-inline-flex align-items-center justify-content-center fw-bold mx-auto
                    ${currentStep >= 1 ? 'text-white' : 'text-secondary'}`}
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    border: currentStep >= 1 ? '2px solid white' : '2px solid #e5e7eb',
                    background: currentStep >= 1 ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' : 'white',
                    boxShadow: currentStep >= 1 ? '0 4px 12px rgba(251, 191, 36, 0.4), inset 0 -2px 0 rgba(0, 0, 0, 0.1)' : '0 2px 6px rgba(0, 0, 0, 0.08)',
                    fontSize: '0.875rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {currentStep > 1 ? <i className="fas fa-check" style={{ fontSize: '0.75rem' }}></i> : '1'}
                </div>
                <div className={`mt-1 fw-semibold ${currentStep >= 1 ? 'text-warning' : 'text-secondary'}`} style={{ fontSize: '0.75rem' }}>
                  Select Items
                </div>
              </div>

              {/* Step 2 */}
              <div className="text-center position-relative" style={{ zIndex: 1, flex: 1 }}>
                <div 
                  className={`rounded-circle d-inline-flex align-items-center justify-content-center fw-bold mx-auto
                    ${currentStep >= 2 ? 'text-white' : 'text-secondary'}`}
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    border: currentStep >= 2 ? '2px solid white' : '2px solid #e5e7eb',
                    background: currentStep >= 2 ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' : 'white',
                    boxShadow: currentStep >= 2 ? '0 4px 12px rgba(251, 191, 36, 0.4), inset 0 -2px 0 rgba(0, 0, 0, 0.1)' : '0 2px 6px rgba(0, 0, 0, 0.08)',
                    fontSize: '0.875rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {currentStep > 2 ? <i className="fas fa-check" style={{ fontSize: '0.75rem' }}></i> : '2'}
                </div>
                <div className={`mt-1 fw-semibold ${currentStep >= 2 ? 'text-warning' : 'text-secondary'}`} style={{ fontSize: '0.75rem' }}>
                  Your Information
                </div>
              </div>

              {/* Step 3 */}
              <div className="text-center position-relative" style={{ zIndex: 1, flex: 1 }}>
                <div 
                  className={`rounded-circle d-inline-flex align-items-center justify-content-center fw-bold mx-auto
                    ${currentStep >= 3 ? 'text-white' : 'text-secondary'}`}
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    border: currentStep >= 3 ? '2px solid white' : '2px solid #e5e7eb',
                    background: currentStep >= 3 ? 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)' : 'white',
                    boxShadow: currentStep >= 3 ? '0 4px 12px rgba(56, 189, 248, 0.4), inset 0 -2px 0 rgba(0, 0, 0, 0.1)' : '0 2px 6px rgba(0, 0, 0, 0.08)',
                    fontSize: '0.875rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {currentStep > 3 ? <i className="fas fa-check" style={{ fontSize: '0.75rem' }}></i> : '3'}
                </div>
                <div className={`mt-1 fw-semibold ${currentStep >= 3 ? 'text-info' : 'text-secondary'}`} style={{ fontSize: '0.75rem' }}>
                  {formValues?.donationType === 'physical' ? 'Drop-Off' : 'Payment'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.97)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.3s ease-out',
        }}>
          <div style={{
            textAlign: 'center',
            padding: 40,
            maxWidth: 500,
            animation: 'fadeIn 0.4s ease-out',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 24,
              position: 'relative',
              width: '100%',
            }}>
              <div style={{
                position: 'absolute',
                width: '110px',
                height: '110px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.12) 0%, rgba(245, 158, 11, 0.06) 100%)',
                border: '2px solid rgba(251, 191, 36, 0.2)',
                boxShadow: '0 0 35px rgba(251, 191, 36, 0.22), inset 0 0 18px rgba(251, 191, 36, 0.12)',
                animation: 'spinPulse 3s ease-in-out infinite',
              }} />
              <div className="spinner-border" role="status" style={{
                width: '4.2rem',
                height: '4.2rem',
                color: '#fbbf24',
                filter: 'drop-shadow(0 3px 12px rgba(251, 191, 36, 0.35))',
                position: 'relative',
                zIndex: 1,
              }}>
                <span className="visually-hidden">Processing...</span>
              </div>
            </div>
            <div style={{
              fontSize: 20,
              color: '#1f2937',
              marginBottom: 12,
              fontWeight: 700,
              letterSpacing: '0.3px',
            }}>
              Yugal Kunj Temple
            </div>
            <div style={{
              fontSize: 24,
              color: '#fbbf24',
              fontWeight: 800,
              marginBottom: 16,
              letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #38bdf8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Processing your donation...
            </div>
            <div style={{
              marginTop: 16,
              color: '#6b7280',
              fontSize: 15,
              fontWeight: 500,
              letterSpacing: '0.3px',
            }}>
              Please do not close this window.
            </div>
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="container mt-3 mb-5">
        <Formik
          enableReinitialize={true}
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, setFieldTouched, validateForm }) => {
            const selectedItems = values.groceryItems.filter((item: any) => Number(item.quantity) > 0);
            const totalAmount = selectedItems.reduce((sum: number, item: any) => sum + (Number(item.quantity) * item.estimatedPrice), 0);
            const hasSelectedItems = selectedItems.length > 0;

            const donorFields = ['donorFirstName', 'donorLastName', 'donorEmail', 'donorPhone'];
            const donorReady = donorFields.every((f) => String((values as any)[f] ?? '').trim().length > 0);

            if (!formValues || formValues !== values) {
              setFormValues(values);
            }

            const handleNext = async () => {
              if (currentStep === 1 && !hasSelectedItems) {
                Swal.fire({
                  icon: 'warning',
                  title: 'No Items Selected',
                  text: 'Please add at least one item to your donation by clicking the + button to increase the quantity.',
                });
                return;
              }
              if (currentStep === 2) {
                const validationErrors = await validateForm();
                const hasErrors = donorFields.some((field) => (validationErrors as any)[field]);
                if (hasErrors) {
                  donorFields.forEach((field) => setFieldTouched(field, true));
                  const firstErrorField = donorFields.find((field) => (validationErrors as any)[field]);
                  if (firstErrorField) {
                    setTimeout(() => {
                      const element = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
                      if (element) {
                        element.focus();
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  }
                  Swal.fire({
                    icon: 'warning',
                    title: 'Incomplete Information',
                    text: 'Please fill in all required fields to continue.',
                  });
                  return;
                }
              }
              setCurrentStep((prev) => Math.min(prev + 1, 3));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            };

            const handleBack = () => {
              setCurrentStep((prev) => Math.max(prev - 1, 1));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            };

            return (
              <Form>
                <div className="row g-4">
                  {/* Left Column - Form */}
                  <div className="col-lg-8">
                    {/* Step 1: Donation Type & Grocery Items */}
                    {currentStep === 1 && (
                    <div className="card card-hover mb-4" style={{ animation: 'slideDown 0.5s ease-out' }}>
                      <div className="card-body p-4">
                        {/* Donation Type Selection */}
                        <div className="mb-3">
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <span className="fw-bold" style={{ fontSize: '0.95rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                                <i className="fas fa-hand-holding-heart me-2" style={{ color: '#6366f1' }}></i>Donation Type:
                              </span>
                              <div className="btn-group" role="group" style={{ border: '1px solid #dee2e6', borderRadius: '25px', overflow: 'hidden' }}>
                                <label className="m-0">
                                  <Field type="radio" name="donationType" value="physical" className="d-none" />
                                  <span 
                                    className="btn"
                                    style={{ 
                                      fontSize: '0.85rem', 
                                      padding: '0.45rem 0.9rem', 
                                      borderRadius: '0',
                                      boxShadow: values.donationType === 'physical' ? 'inset 0 1px 0 rgba(255,255,255,0.2)' : 'none',
                                      background: values.donationType === 'physical' ? 'linear-gradient(145deg, #6366f1, #4f46e5)' : '#fff',
                                      color: values.donationType === 'physical' ? '#fff' : '#6c757d',
                                      border: 'none',
                                      transition: 'all 0.3s ease'
                                    }}
                                  >
                                    📦 Drop-Off
                                  </span>
                                </label>
                                <label className="m-0">
                                  <Field type="radio" name="donationType" value="online" className="d-none" />
                                  <span 
                                    className="btn"
                                    style={{ 
                                      fontSize: '0.85rem', 
                                      padding: '0.45rem 0.9rem', 
                                      borderRadius: '0',
                                      boxShadow: values.donationType === 'online' ? 'inset 0 1px 0 rgba(255,255,255,0.2)' : 'none',
                                      background: values.donationType === 'online' ? 'linear-gradient(145deg, #6366f1, #4f46e5)' : '#fff',
                                      color: values.donationType === 'online' ? '#fff' : '#6c757d',
                                      border: 'none',
                                      borderLeft: '1px solid #dee2e6',
                                      transition: 'all 0.3s ease'
                                    }}
                                  >
                                    💳 Online
                                  </span>
                                </label>
                              </div>
                            </div>
                            <button
                              type="button"
                              className={`btn btn-primary nav-btn-small ${hasSelectedItems ? 'yk-attention' : ''}`}
                              onClick={handleNext}
                              style={{
                                borderRadius: '20px',
                                boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
                                background: 'linear-gradient(145deg, #6366f1, #4f46e5)',
                                border: 'none',
                                padding: '0.5rem 1.2rem'
                              }}
                            >
                              Next <i className="fas fa-arrow-right ms-1"></i>
                            </button>
                          </div>
                          <ErrorMessage name="donationType" component="div" className="text-danger small mt-2" />
                        </div>

                        {/* Grocery Items Selection */}
                        <div className="d-flex align-items-center mb-4">
                          <div className="icon-box me-3" style={{ width: '45px', height: '45px', fontSize: '1.3rem' }}>
                            <i className="fas fa-leaf"></i>
                          </div>
                          <div>
                            <h5 className="mb-0 fw-bold" style={{ fontSize: '1.1rem', letterSpacing: '0.5px', color: '#1e293b' }}>Select Grocery Items</h5>
                            <p className="small text-muted mb-0" style={{ fontSize: '0.85rem' }}>Choose items you'd like to donate</p>
                          </div>
                        </div>

                        <div className="row g-3">
                          <FieldArray name="groceryItems">
                            {() => (
                              <>
                                {values.groceryItems.map((item, index) => {
                                  const groceryItem = groceryItems.find((g: any) => g.id === item.itemId);
                                  const isSelected = Number(item.quantity) > 0;
                                  return (
                                    <div key={item.itemId} className="col-md-4">
                                      <div className={`item-card p-3 text-center ${isSelected ? 'selected' : ''}`}>
                                        {groceryItem?.itemImage_URL ? (
                                          <img 
                                            src={groceryItem.itemImage_URL} 
                                            alt={item.itemName}
                                            className="item-image"
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).style.display = 'none';
                                              (e.target as HTMLImageElement).nextElementSibling?.classList.remove('d-none');
                                            }}
                                          />
                                        ) : null}
                                        <span className={`item-icon ${groceryItem?.itemImage_URL ? 'd-none' : ''}`}>{groceryItem?.icon || '🛒'}</span>
                                        <h6 className="mb-2 fw-bold" style={{ 
                                          color: isSelected ? 'white' : '#1f2937',
                                          fontSize: '1rem',
                                          lineHeight: '1.4'
                                        }}>
                                          {item.itemName}
                                        </h6>
                                        {values.donationType === 'online' && (
                                          <div className="d-flex justify-content-between align-items-center mb-2 px-2">
                                            <span style={{ 
                                              color: isSelected ? 'rgba(255,255,255,0.9)' : '#6b7280',
                                              fontSize: '0.9rem',
                                              fontWeight: '500'
                                            }}>
                                              Price:
                                            </span>
                                            <span className="fw-bold" style={{ 
                                              color: isSelected ? 'white' : '#fbbf24',
                                              fontSize: '1rem'
                                            }}>
                                              ${item.estimatedPrice}
                                            </span>
                                          </div>
                                        )}
                                        <div className="mb-2">
                                          <FieldArray name="groceryItems">
                                            {({ form }) => (
                                              <div className="quantity-selector">
                                                <button
                                                  type="button"
                                                  className="quantity-btn"
                                                  onClick={() => {
                                                    const currentQty = Number(item.quantity) || 0;
                                                    if (currentQty > 0) {
                                                      form.setFieldValue(`groceryItems.${index}.quantity`, currentQty - 1);
                                                    }
                                                  }}
                                                >
                                                  −
                                                </button>
                                                <div className="quantity-display">
                                                  {item.quantity || 0}
                                                </div>
                                                <button
                                                  type="button"
                                                  className="quantity-btn"
                                                  onClick={() => {
                                                    const currentQty = Number(item.quantity) || 0;
                                                    form.setFieldValue(`groceryItems.${index}.quantity`, currentQty + 1);
                                                  }}
                                                >
                                                  +
                                                </button>
                                              </div>
                                            )}
                                          </FieldArray>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </FieldArray>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Step 2: Your Information */}
                    {currentStep === 2 && (
                      <div className="card card-hover mb-4" style={{ animation: 'slideDown 0.5s ease-out' }}>
                      <div className="card-body p-4">
                        {/* Navigation Buttons and Title - Top */}
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <div className="d-flex align-items-center">
                            <div className="icon-box me-3" style={{ width: '45px', height: '45px', fontSize: '1.3rem' }}>
                              <i className="fas fa-user"></i>
                            </div>
                            <h5 className="mb-0 fw-bold" style={{ fontSize: '1.1rem', letterSpacing: '0.5px', color: '#1e293b' }}>Your Information</h5>
                          </div>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary nav-btn-small"
                              onClick={handleBack}
                            >
                              <i className="fas fa-arrow-left me-1"></i> Back
                            </button>
                            <button
                              type="button"
                              className={`btn btn-primary nav-btn-small ${donorReady ? 'yk-attention' : ''}`}
                              onClick={handleNext}
                            >
                              Next <i className="fas fa-arrow-right ms-1"></i>
                            </button>
                          </div>
                        </div>

                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label fw-bold">First Name *</label>
                            <Field
                              type="text"
                              name="donorFirstName"
                              placeholder="First name"
                              className={`form-control form-control-lg ${errors.donorFirstName && touched.donorFirstName ? 'is-invalid' : ''}`}
                            />
                            <ErrorMessage name="donorFirstName" component="div" className="text-danger small mt-1" />
                          </div>

                          <div className="col-md-6">
                            <label className="form-label fw-bold">Last Name *</label>
                            <Field
                              type="text"
                              name="donorLastName"
                              placeholder="Last name"
                              className={`form-control form-control-lg ${errors.donorLastName && touched.donorLastName ? 'is-invalid' : ''}`}
                            />
                            <ErrorMessage name="donorLastName" component="div" className="text-danger small mt-1" />
                          </div>

                          <div className="col-md-6">
                            <label className="form-label fw-bold">Email Address *</label>
                            <Field
                              type="email"
                              name="donorEmail"
                              placeholder="your.email@example.com"
                              className={`form-control form-control-lg ${errors.donorEmail && touched.donorEmail ? 'is-invalid' : ''}`}
                            />
                            <ErrorMessage name="donorEmail" component="div" className="text-danger small mt-1" />
                          </div>

                          <div className="col-md-6">
                            <label className="form-label fw-bold">Phone Number *</label>
                            <Field name="donorPhone">
                              {({ field }: any) => (
                                <input
                                  {...field}
                                  type="tel"
                                  inputMode="numeric"
                                  placeholder="(555) 123-4567"
                                  className={`form-control form-control-lg ${errors.donorPhone && touched.donorPhone ? 'is-invalid' : ''}`}
                                  onChange={(e) => {
                                    const digits = String(e.target.value || '').replace(/\D/g, '').slice(0, 10);
                                    setFieldValue('donorPhone', digits);
                                  }}
                                />
                              )}
                            </Field>
                            <ErrorMessage name="donorPhone" component="div" className="text-danger small mt-1" />
                          </div>

                        </div>
                      </div>
                    </div>
                    )}

                    {/* Step 3: Payment Details */}
                    {currentStep === 3 && (
                      <div className="card card-hover mb-3" style={{ animation: 'slideDown 0.5s ease-out' }}>
                      <div className="card-body p-3">
                        {/* Navigation Buttons and Title - Top */}
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <div className="d-flex align-items-center">
                            <div className="icon-box me-3" style={{ width: '45px', height: '45px', fontSize: '1.3rem' }}>
                              <i className={`fas fa-${values.donationType === 'physical' ? 'truck' : 'credit-card'}`}></i>
                            </div>
                            <h5 className="mb-0 fw-bold" style={{ fontSize: '1.1rem', letterSpacing: '0.5px', color: '#1e293b' }}>{values.donationType === 'physical' ? 'Drop-Off Details' : 'Payment Details'}</h5>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-secondary nav-btn-small"
                            onClick={handleBack}
                          >
                            <i className="fas fa-arrow-left me-1"></i> Back
                          </button>
                        </div>

                        {/* Physical Drop-Off Details */}
                        {values.donationType === 'physical' && (
                          <div className="payment-fields-container">
                            <div className="alert alert-info mb-4">
                              <h6 className="fw-bold mb-2">
                                <i className="fas fa-map-marker-alt me-2"></i>Temple Address
                              </h6>
                              <p className="mb-0">
                                Yugal Kunj – Radha Krishna Temple and Community Center<br />
                                2769 Duluth Hwy<br />
                                Duluth, GA 30096
                              </p>
                            </div>
                            <div className="row g-3">
                              <div className="col-md-6">
                                <label className="form-label fw-bold">Preferred Drop-Off Date *</label>
                                <Field
                                  type="date"
                                  name="dropOffDate"
                                  min={new Date().toISOString().split('T')[0]}
                                  className="form-control form-control-lg"
                                />
                                <ErrorMessage name="dropOffDate" component="div" className="text-danger small mt-1" />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label fw-bold">Preferred Drop-Off Time *</label>
                                <Field as="select" name="dropOffTime" className={`form-select form-select-lg ${errors.dropOffTime && touched.dropOffTime ? 'is-invalid' : ''}`}>
                                  <option value="">Select Time</option>
                                  <option value="09:00 AM">09:00 AM</option>
                                  <option value="10:00 AM">10:00 AM</option>
                                  <option value="11:00 AM">11:00 AM</option>
                                  <option value="12:00 PM">12:00 PM</option>
                                  <option value="01:00 PM">01:00 PM</option>
                                  <option value="02:00 PM">02:00 PM</option>
                                  <option value="03:00 PM">03:00 PM</option>
                                  <option value="04:00 PM">04:00 PM</option>
                                  <option value="05:00 PM">05:00 PM</option>
                                </Field>
                                <ErrorMessage name="dropOffTime" component="div" className="text-danger small mt-1" />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Online Payment Method & Fields */}
                        {values.donationType === 'online' && (
                          <div className="payment-fields-container" style={{ marginTop: '0' }}>
                            {/* Donation Frequency and Payment Method - Same Row */}
                            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
                              <div className="d-flex align-items-center">
                                <span className="fw-bold me-2" style={{ fontSize: '0.85rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                                  <i className="fas fa-calendar-alt me-1" style={{ color: '#6366f1' }}></i>Frequency:
                                </span>
                                <div className="btn-group" role="group" style={{ border: '1px solid #dee2e6', borderRadius: '20px', overflow: 'hidden' }}>
                                  <label className="m-0">
                                    <Field type="radio" name="donationFrequency" value="onetime" className="d-none" />
                                    <span 
                                      className="btn"
                                      style={{ 
                                        fontSize: '0.8rem', 
                                        padding: '0.4rem 0.85rem', 
                                        borderRadius: '0',
                                        boxShadow: values.donationFrequency === 'onetime' ? 'inset 0 1px 0 rgba(255,255,255,0.2)' : 'none',
                                        background: values.donationFrequency === 'onetime' ? 'linear-gradient(145deg, #6366f1, #4f46e5)' : '#fff',
                                        color: values.donationFrequency === 'onetime' ? '#fff' : '#6c757d',
                                        border: 'none',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      One-Time
                                    </span>
                                  </label>
                                  <label className="m-0">
                                    <Field type="radio" name="donationFrequency" value="recurring" className="d-none" />
                                    <span 
                                      className="btn"
                                      style={{ 
                                        fontSize: '0.8rem', 
                                        padding: '0.4rem 0.85rem', 
                                        borderRadius: '0',
                                        boxShadow: values.donationFrequency === 'recurring' ? 'inset 0 1px 0 rgba(255,255,255,0.2)' : 'none',
                                        background: values.donationFrequency === 'recurring' ? 'linear-gradient(145deg, #6366f1, #4f46e5)' : '#fff',
                                        color: values.donationFrequency === 'recurring' ? '#fff' : '#6c757d',
                                        border: 'none',
                                        borderLeft: '1px solid #dee2e6',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      Recurring
                                    </span>
                                  </label>
                                </div>
                              </div>
                              <div className="d-flex align-items-center">
                                <span className="fw-bold me-2" style={{ fontSize: '0.85rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                                  <i className="fas fa-credit-card me-1" style={{ color: '#6366f1' }}></i>Pay via:
                                </span>
                                <div className="btn-group" role="group" style={{ border: '1px solid #dee2e6', borderRadius: '20px', overflow: 'hidden' }}>
                                  <label className="m-0">
                                    <Field type="radio" name="paymentMethod" value="creditcard" className="d-none" />
                                    <span 
                                      className="btn"
                                      style={{ 
                                        fontSize: '0.8rem', 
                                        padding: '0.4rem 0.85rem', 
                                        borderRadius: '0',
                                        boxShadow: values.paymentMethod === 'creditcard' ? 'inset 0 1px 0 rgba(255,255,255,0.2)' : 'none',
                                        background: values.paymentMethod === 'creditcard' ? 'linear-gradient(145deg, #6366f1, #4f46e5)' : '#fff',
                                        color: values.paymentMethod === 'creditcard' ? '#fff' : '#6c757d',
                                        border: 'none',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      💳 Card
                                    </span>
                                  </label>
                                  <label className="m-0">
                                    <Field type="radio" name="paymentMethod" value="ach" className="d-none" />
                                    <span 
                                      className="btn"
                                      style={{ 
                                        fontSize: '0.8rem', 
                                        padding: '0.4rem 0.85rem', 
                                        borderRadius: '0',
                                        boxShadow: values.paymentMethod === 'ach' ? 'inset 0 1px 0 rgba(255,255,255,0.2)' : 'none',
                                        background: values.paymentMethod === 'ach' ? 'linear-gradient(145deg, #6366f1, #4f46e5)' : '#fff',
                                        color: values.paymentMethod === 'ach' ? '#fff' : '#6c757d',
                                        border: 'none',
                                        borderLeft: '1px solid #dee2e6',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      🏦 ACH
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </div>
                            <ErrorMessage name="paymentMethod" component="div" className="text-danger small mb-2" />

                            {/* Recurring Start Date and Subscription End Type */}
                            {values.donationFrequency === 'recurring' && (
                              <>
                                <div className="row g-3 mb-3">
                                  <div className="col-md-6">
                                    <label htmlFor="recurringStartDate" className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Start Date *</label>
                                    <Field name="recurringStartDate">
                                      {({ field }: any) => (
                                        <input
                                          {...field}
                                          type="date"
                                          id="recurringStartDate"
                                          className={`form-control ${touched.recurringStartDate && errors.recurringStartDate ? 'is-invalid' : ''}`}
                                          style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                          min={todayStr}
                                          required
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setFieldValue('recurringStartDate', v);
                                            // Update end date to 1 year from start when EndsOnDate is selected
                                            if (values.subscriptionEndType === 'EndsOnDate') {
                                              try {
                                                const d = new Date(v + 'T12:00:00');
                                                if (!isNaN(d.getTime())) {
                                                  const end = new Date(d);
                                                  end.setFullYear(d.getFullYear() + 1);
                                                  const endStr = end.toISOString().slice(0, 10);
                                                  setFieldValue('recurringEndDate', endStr);
                                                }
                                              } catch {}
                                            }
                                          }}
                                        />
                                      )}
                                    </Field>
                                    <ErrorMessage name="recurringStartDate" component="div" className="text-danger small mt-1" />
                                  </div>
                                  <div className="col-md-6">
                                    <label htmlFor="subscriptionEndType" className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Subscription End *</label>
                                    <Field
                                      as="select"
                                      name="subscriptionEndType"
                                      className={`form-select ${touched.subscriptionEndType && errors.subscriptionEndType ? 'is-invalid' : ''}`}
                                      style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                      onChange={(e: any) => {
                                        const newValue = e.target.value;
                                        setFieldValue('subscriptionEndType', newValue);
                                        // Reset dependent fields when changing type
                                        if (newValue === 'NoEndDate') {
                                          setFieldValue('recurringEndDate', '');
                                          setFieldValue('numberOfTerms', '');
                                        } else if (newValue === 'EndsOnDate') {
                                          setFieldValue('numberOfTerms', '');
                                          // Set default end date to 1 year from start
                                          const start = values.recurringStartDate;
                                          if (start) {
                                            const d = new Date(start + 'T12:00:00');
                                            d.setFullYear(d.getFullYear() + 1);
                                            setFieldValue('recurringEndDate', d.toISOString().slice(0, 10));
                                          }
                                        } else if (newValue === 'EndsAfterTerms') {
                                          setFieldValue('recurringEndDate', '');
                                          setFieldValue('numberOfTerms', '12');
                                        }
                                      }}
                                    >
                                      <option value="NoEndDate">No End Date</option>
                                      <option value="EndsOnDate">Ends On This Date</option>
                                      <option value="EndsAfterTerms">Ends After (Number of Terms)</option>
                                    </Field>
                                    <ErrorMessage name="subscriptionEndType" component="div" className="text-danger small mt-1" />
                                  </div>
                                </div>
                                
                                {/* Show End Date field only when "Ends On This Date" is selected */}
                                {values.subscriptionEndType === 'EndsOnDate' && (
                                  <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                      <label htmlFor="recurringEndDate" className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>End Date *</label>
                                      <Field name="recurringEndDate">
                                        {({ field }: any) => (
                                          <input
                                            {...field}
                                            type="date"
                                            id="recurringEndDate"
                                            className={`form-control ${touched.recurringEndDate && errors.recurringEndDate ? 'is-invalid' : ''}`}
                                            style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                            min={(() => {
                                              const s = values.recurringStartDate;
                                              if (!s) return todayStr;
                                              const d = new Date(s + 'T12:00:00');
                                              d.setMonth(d.getMonth() + 2); // At least 2 months after start
                                              return d.toISOString().slice(0, 10);
                                            })()}
                                            required
                                          />
                                        )}
                                      </Field>
                                      <ErrorMessage name="recurringEndDate" component="div" className="text-danger small mt-1" />
                                    </div>
                                  </div>
                                )}
                                
                                {/* Show Number of Terms field only when "Ends After" is selected */}
                                {values.subscriptionEndType === 'EndsAfterTerms' && (
                                  <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                      <label htmlFor="numberOfTerms" className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Number of Monthly Payments *</label>
                                      <Field
                                        type="number"
                                        name="numberOfTerms"
                                        id="numberOfTerms"
                                        className={`form-control ${touched.numberOfTerms && errors.numberOfTerms ? 'is-invalid' : ''}`}
                                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                        min="2"
                                        max="9999"
                                        placeholder="e.g., 12"
                                      />
                                      <ErrorMessage name="numberOfTerms" component="div" className="text-danger small mt-1" />
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Credit Card Fields */}
                            {values.paymentMethod === 'creditcard' && (
                              <div className="row g-2">
                                <div className="col-12">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Card Number *</label>
                                  <Field name="cardNumber">
                                    {({ field, form }: any) => (
                                      <input
                                        type="text"
                                        placeholder="4444 4444 4444 4444"
                                        maxLength={19}
                                        className="form-control"
                                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                        value={field.value || ''}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(/\s/g, '');
                                          const formatted = value.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
                                          form.setFieldValue('cardNumber', formatted);
                                        }}
                                        onBlur={field.onBlur}
                                      />
                                    )}
                                  </Field>
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Expiry Date *</label>
                                  <Field name="expiryDate">
                                    {({ field, form }: any) => (
                                      <input
                                        type="text"
                                        placeholder="MM/YY"
                                        maxLength={5}
                                        className="form-control"
                                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                        value={field.value || ''}
                                        onChange={(e) => {
                                          let value = e.target.value.replace(/\D/g, '');
                                          if (value.length >= 2) {
                                            value = value.slice(0, 2) + '/' + value.slice(2, 4);
                                          }
                                          form.setFieldValue('expiryDate', value);
                                        }}
                                        onBlur={field.onBlur}
                                      />
                                    )}
                                  </Field>
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>CVV *</label>
                                  <Field name="cvv">
                                    {({ field, form }: any) => (
                                      <input
                                        type="text"
                                        placeholder="123"
                                        maxLength={4}
                                        className="form-control"
                                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                        value={field.value || ''}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(/\D/g, '');
                                          form.setFieldValue('cvv', value);
                                        }}
                                        onBlur={field.onBlur}
                                      />
                                    )}
                                  </Field>
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Cardholder Name *</label>
                                  <Field
                                    type="text"
                                    name="cardholderName"
                                    placeholder="Name on card"
                                    className="form-control"
                                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Billing ZIP Code *</label>
                                  <Field name="billingZipCode">
                                    {({ field, form }: any) => (
                                      <input
                                        type="text"
                                        placeholder="12345"
                                        maxLength={10}
                                        className={`form-control ${touched.billingZipCode && errors.billingZipCode ? 'is-invalid' : ''}`}
                                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                        value={field.value || ''}
                                        onChange={(e) => {
                                          const digits = e.target.value.replace(/\D/g, '');
                                          let formatted = digits;
                                          if (digits.length > 5) {
                                            formatted = digits.slice(0, 5) + '-' + digits.slice(5, 9);
                                          }
                                          form.setFieldValue('billingZipCode', formatted);
                                        }}
                                        onBlur={field.onBlur}
                                      />
                                    )}
                                  </Field>
                                  <ErrorMessage name="billingZipCode" component="div" className="text-danger small mt-1" />
                                </div>
                              </div>
                            )}

                            {/* ACH Fields */}
                            {values.paymentMethod === 'ach' && (
                              <div className="row g-2">
                                <div className="col-12">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Bank Name *</label>
                                  <Field
                                    type="text"
                                    name="bankName"
                                    placeholder="Bank name"
                                    className="form-control"
                                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Account Number *</label>
                                  <Field
                                    type="text"
                                    name="accountNumber"
                                    placeholder="Account number"
                                    className="form-control"
                                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Routing Number *</label>
                                  <Field
                                    type="text"
                                    name="routingNumber"
                                    placeholder="Routing number"
                                    className="form-control"
                                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Account Holder Name *</label>
                                  <Field
                                    type="text"
                                    name="accountHolderName"
                                    placeholder="Account holder name"
                                    className="form-control"
                                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Account Type *</label>
                                  <Field name="achAccountType">
                                    {({ field }: any) => (
                                      <select 
                                        {...field}
                                        className={`form-select ${touched.achAccountType && errors.achAccountType ? 'is-invalid' : ''}`}
                                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                      >
                                        <option value="Checking">Checking</option>
                                        <option value="Savings">Savings</option>
                                      </select>
                                    )}
                                  </Field>
                                  <ErrorMessage name="achAccountType" component="div" className="text-danger small mt-1" />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Billing ZIP Code *</label>
                                  <Field name="billingZipCode">
                                    {({ field, form }: any) => (
                                      <input
                                        type="text"
                                        placeholder="12345"
                                        maxLength={10}
                                        className={`form-control ${touched.billingZipCode && errors.billingZipCode ? 'is-invalid' : ''}`}
                                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                                        value={field.value || ''}
                                        onChange={(e) => {
                                          const digits = e.target.value.replace(/\D/g, '');
                                          let formatted = digits;
                                          if (digits.length > 5) {
                                            formatted = digits.slice(0, 5) + '-' + digits.slice(5, 9);
                                          }
                                          form.setFieldValue('billingZipCode', formatted);
                                        }}
                                        onBlur={field.onBlur}
                                      />
                                    )}
                                  </Field>
                                  <ErrorMessage name="billingZipCode" component="div" className="text-danger small mt-1" />
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* reCAPTCHA - Only for Online Payments */}
                        {values.donationType === 'online' && (
                          <div className="mt-2" style={{ 
                            minHeight: '78px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            position: 'relative',
                            zIndex: 1
                          }}>
                            <div id="recaptcha-container" style={{
                              transform: 'scale(1)',
                              transformOrigin: '0 0',
                              marginBottom: '8px',
                              width: '100%',
                              maxWidth: '304px'
                            }}></div>
                            {!recaptchaToken && (
                              <div className="text-danger small mt-2">
                                <i className="fas fa-exclamation-circle me-1"></i>
                                Please complete reCAPTCHA verification
                              </div>
                            )}
                            {recaptchaToken && (
                              <div className="text-success small mt-2">
                                <i className="fas fa-check-circle me-1"></i>
                                reCAPTCHA verified
                              </div>
                            )}
                          </div>
                        )}

                        {/* Special Instructions */}
                        <div className="mt-2">
                          <label className="form-label fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                            <i className="fas fa-comment-dots me-2" style={{ fontSize: '0.8rem' }}></i>
                            Special Instructions (Optional)
                          </label>
                          <Field
                            as="textarea"
                            name="specialInstructions"
                            placeholder="Any special requests or delivery instructions..."
                            rows={3}
                            className="form-control"
                            style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
                          />
                        </div>

                        {/* Submit Button */}
                        <div className="mt-3 d-flex justify-content-end">
                          <button
                            type="submit"
                            className="btn btn-donate-3d"
                            style={{ padding: '0.6rem 1.75rem', fontSize: '0.95rem' }}
                            disabled={loading || (values.donationType === 'online' && !recaptchaToken)}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-hand-holding-usd me-2"></i>
                                Donate Now
                              </>
                            )}
                          </button>
                        </div>

                      </div>
                    </div>
                    )}
                  </div>

                  {/* Right Column - Summary */}
                  <div className="col-lg-4 donation-summary-sidebar">
                    <div style={{ position: 'sticky', top: '20px' }}>
                      {selectedItems.length > 0 ? (
                        <div className="summary-card rounded-4 p-3" style={{ minHeight: '300px' }}>
                          <div className="d-flex align-items-center mb-3">
                            <i className="fas fa-check-circle me-2" style={{ fontSize: '1.1rem', color: '#1e293b' }}></i>
                            <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Donation Summary</h5>
                          </div>

                          <div className="row g-3">
                            {/* 1. Selected Items with Total Quantity */}
                            <div className="col-12">
                              <p className="mb-2 fw-semibold" style={{ fontSize: '0.85rem', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Selected Items</p>
                              <div className="yk-summary-items">
                                {selectedItems.map((item, idx) => {
                                  const groceryItem = groceryItems.find((g: any) => g.Id === item.itemId);
                                  return (
                                    <div key={idx} style={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between', 
                                      alignItems: 'center', 
                                      padding: '0.5rem 0.75rem', 
                                      marginBottom: '0.4rem', 
                                      background: 'rgba(255, 255, 255, 0.25)', 
                                      borderRadius: '8px',
                                      transition: 'all 0.2s ease'
                                    }}>
                                      <div className="d-flex align-items-center gap-2">
                                        <span style={{ fontSize: '1.2rem' }}>{groceryItem?.icon || '🛒'}</span>
                                        <div>
                                          <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.2' }}>{item.itemName}</div>
                                          <div style={{ fontSize: '0.7rem', color: '#475569' }}>Qty: {item.quantity}</div>
                                        </div>
                                      </div>
                                      {values.donationType === 'online' && (
                                        <div className="fw-bold" style={{ fontSize: '0.85rem', color: '#1e293b' }}>${(Number(item.quantity) * item.estimatedPrice).toFixed(2)}</div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <div style={{ background: 'rgba(255, 255, 255, 0.35)', borderRadius: '10px', padding: '0.75rem 1rem', marginTop: '0.5rem' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span style={{ fontSize: '0.8rem', color: '#1e293b', fontWeight: '600' }}>Total Items</span>
                                  <span className="fw-bold" style={{ color: '#1e293b', fontSize: '1.5rem' }}>{selectedItems.reduce((sum, item) => sum + Number(item.quantity), 0)}</span>
                                </div>
                              </div>
                            </div>

                            {/* 2. Payment Details */}
                            <div className="col-12">
                              <div style={{ background: 'rgba(255, 255, 255, 0.25)', borderRadius: '10px', padding: '0.75rem 1rem' }}>
                                <p className="mb-2 fw-semibold" style={{ fontSize: '0.85rem', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment Details</p>
                                
                                {values.donationType === 'online' ? (
                                  <div>
                                    <div className="d-flex align-items-center mb-2">
                                      <div style={{ 
                                        width: '32px', 
                                        height: '32px', 
                                        borderRadius: '8px', 
                                        background: 'rgba(255,255,255,0.35)', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        marginRight: '10px',
                                        fontSize: '1.1rem'
                                      }}>
                                        {values.donationFrequency === 'recurring' ? '🔄' : '🎁'}
                                      </div>
                                      <div>
                                        <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.2' }}>
                                          {values.donationFrequency === 'recurring' ? 'Recurring' : 'One-Time'}
                                        </div>
                                        {values.donationFrequency === 'recurring' && values.recurringFrequency && (
                                          <div style={{ fontSize: '0.7rem', color: '#475569' }}>
                                            {values.recurringFrequency.charAt(0).toUpperCase() + values.recurringFrequency.slice(1)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                      <div style={{ 
                                        width: '32px', 
                                        height: '32px', 
                                        borderRadius: '8px', 
                                        background: 'rgba(255,255,255,0.35)', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        marginRight: '10px',
                                        fontSize: '1.1rem'
                                      }}>
                                        {values.paymentMethod === 'creditcard' ? '💳' : '🏦'}
                                      </div>
                                      <div>
                                        <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.2' }}>
                                          {values.paymentMethod === 'creditcard' ? 'Credit Card' : 'ACH Transfer'}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="d-flex align-items-center mb-2">
                                      <div style={{ 
                                        width: '32px', 
                                        height: '32px', 
                                        borderRadius: '8px', 
                                        background: 'rgba(255,255,255,0.35)', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        marginRight: '10px',
                                        fontSize: '1.1rem'
                                      }}>
                                        📦
                                      </div>
                                      <div>
                                        <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.2' }}>Physical Drop-Off</div>
                                      </div>
                                    </div>
                                    {values.dropOffDate && values.dropOffTime && (
                                      <div className="d-flex align-items-center mt-2">
                                        <div style={{ 
                                          width: '32px', 
                                          height: '32px', 
                                          borderRadius: '8px', 
                                          background: 'rgba(255,255,255,0.3)', 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          justifyContent: 'center',
                                          marginRight: '10px',
                                          fontSize: '1rem'
                                        }}>
                                          📅
                                        </div>
                                        <div>
                                          <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.2' }}>{new Date(values.dropOffDate).toLocaleDateString()}</div>
                                          <div style={{ fontSize: '0.7rem', color: '#475569' }}>{values.dropOffTime}</div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* 3. Total Amount - Only show for online donations */}
                            {values.donationType === 'online' && (
                              <div className="col-12">
                                <div style={{ background: 'rgba(255, 255, 255, 0.35)', borderRadius: '10px', padding: '1rem', border: '2px solid rgba(255,255,255,0.5)' }}>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span style={{ fontSize: '0.8rem', color: '#1e293b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Amount</span>
                                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.8rem', color: '#1e293b' }}>${totalAmount.toFixed(2)}</h2>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="empty-summary rounded-4 text-center p-5">
                          <i className="fas fa-shopping-cart" style={{ fontSize: '4rem', color: '#fbbf24', marginBottom: '1.5rem', opacity: 0.5 }}></i>
                          <h5 className="fw-bold mb-2" style={{ color: '#fbbf24' }}>No Items Selected</h5>
                          <p className="text-muted mb-0">Select grocery items to see your donation summary</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default DonateGrocery;
